<template>
  <!--Loading Form Details-->
  <div v-if="isLoadingInit" class="d-flex flex-column align-center justify-center">
    <VProgressCircular indeterminate color="primary" class="my-8" />
  </div>
  <!--Form Status Pending-->
  <Card v-else-if="isFormPending">
    <VContainer v-if="!isFormSuccessfullySubmited">
      <!-- Header -->
      <div class="form-header mb-8">
        <h1 class="form-title">State Pension Form</h1>
        <div class="title-bar" />
      </div>
      <!-- Step Indicators -->
      <div class="steps-indicators">
        <div
          v-for="(step, index) in menuItems"
          :key="index"
          class="step-dot"
          :class="{
            active: index + 1 === currentStep,
            completed: index + 1 < currentStep
          }"
          @click="goToStep(index + 1)"
        >
          <span class="step-number">{{ index + 1 }}</span>
          <span class="step-label">{{ step.label }}</span>
        </div>
      </div>
      <!-- Form Card -->
      <div>
        <!-- ──────────────── STEP 0 ──────────────── -->
        <VForm v-if="currentStep === 0 && isAgentForm" ref="form0" v-model="valid[0]" lazy-validation>
          <h2 class="step-title mb-6">Agent Information</h2>
          <VRow>
            <VCol cols="12" sm="6">
              <VSelect
                label="Validation Agent"
                :items="agentsOptions"
                v-model="formData.agent"
                item-title="label"
                item-value="value"
                variant="outlined"
                :disabled="isLoadingSave"
                class="custom-input"
                :rules="[rules.agentRequired]"
              />
            </VCol>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 1 ──────────────── -->
        <VForm v-if="currentStep === 1" ref="form1" v-model="valid[1]" lazy-validation>
          <h2 class="step-title mb-6">1. Friend / Family for Communication</h2>
          <VRow>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.firstName"
                label="First name"
                placeholder="e.g. John"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.lastName"
                label="Last name"
                placeholder="e.g. Doe"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.tel"
                label="Tel"
                placeholder="e.g. (083) 8112345"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-model="formData.contactCommunication.email"
                label="Email"
                type="email"
                placeholder="e.g. name@example.com"
                variant="outlined"
                :rules="[rules.agentRequired, rules.email]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.relationship"
                label="Relationship to Claimant"
                placeholder="e.g. Daughter / Friend"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 2 ──────────────── -->
        <VForm v-if="currentStep === 2" ref="form2" v-model="valid[2]" lazy-validation>
          <h2 class="step-title mb-6">2. About the Claimant</h2>

          <VRow>
            <VCol cols="12" sm="4">
              <VSelect
                name="salutation"
                v-model="formData.claimant.title"
                label="Title"
                :items="titleOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="8">
              <VTextField
                name="firstName"
                v-capitalize
                v-model="formData.claimant.firstName"
                label="First Name"
                placeholder="Eg. Marry"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="surname"
                v-capitalize
                v-model="formData.claimant.surname"
                label="Surname / Family Name"
                placeholder="e.g. Doe"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="middleName"
                v-capitalize
                v-model="formData.claimant.middleName"
                label="Middle Name(s)"
                placeholder="eg. Walsh"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="maidenName"
                v-capitalize
                v-model="formData.claimant.maidenName"
                label="Maiden Names"
                placeholder="Full maiden name(s)"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="otherNames"
                v-capitalize
                v-model="formData.claimant.otherNames"
                label="Other/Previous Names "
                placeholder="Other/Previous name(s)"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VDateInput
                name="dob"
                v-model="formData.claimant.dob"
                label="Date of Birth"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="maxDate"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" v-if="newStatePensionWarning">
              <VAlert type="warning" variant="tonal" density="comfortable">{{ newStatePensionWarning }}</VAlert>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="phone"
                v-capitalize
                v-model="formData.claimant.tel"
                label="Tel"
                placeholder="e.g. (083) 8112345"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="email"
                v-model="formData.claimant.email"
                label="Email"
                type="email"
                placeholder="e.g. name@example.com"
                variant="outlined"
                :rules="[rules.email]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VSelect
                name="maritalStatus"
                v-model="formData.claimant.maritalStatus"
                label="Marital Status"
                :items="maritalStatusOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="nationality"
                v-capitalize
                v-model="formData.claimant.nationality"
                label="Nationality"
                placeholder="e.g. Which passport have you?"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>

            <VCol cols="12" sm="6" v-if="['Married', 'Civil Partner'].includes(formData.claimant.maritalStatus)">
              <VDateInput
                name="dateOfMarriage"
                v-model="formData.claimant.dateOfMarriage"
                label="Date of Marriage"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="12" v-if="formData.claimant.maritalStatus === 'Widowed'">
              <VDateInput
                name="dateOfBereavement"
                v-model="formData.claimant.dateOfBereavement"
                label="Date of Bereavement"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="12" v-if="['Divorced', 'Separated'].includes(formData.claimant.maritalStatus)">
              <VDateInput
                name="dateOfDivorce"
                v-model="formData.claimant.dateOfDivorce"
                label="Date of Divorce / Civil Dissolution"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>

            <!-- UK NINO -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12">
              <div class="field-label">“Ever Had a UK Nat Ins No”?</div>
              <VBtnToggle v-model="formData.claimant.hasNino" mandatory color="teal" rounded="lg" border class="mt-4">
                <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>
            <VCol cols="12" v-if="formData.claimant.hasNino === true">
              <VTextField
                name="nino"
                v-capitalize
                v-model="formData.claimant.nino"
                label="National Insurance Number"
                placeholder="e.g. NX123456B"
                variant="outlined"
                :rules="[rules.required, rules.nino]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>

            <!-- Address -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12"><div class="field-label">Address</div></VCol>
            <VCol cols="12" sm="6">
              <VSelect
                name="country"
                v-model="formData.claimant.country"
                label="Country"
                :items="countryOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="postcode"
                v-capitalize
                v-model="formData.claimant.postcode"
                label="Postcode"
                placeholder="e.g. F28 AE22"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol v-if="formData.claimant.country" cols="12">
              <AddressSearch
                :country="formData.claimant.country || 'GBR'"
                @address-selected="onClaimantAddressSelected"
              >
              </AddressSearch>
            </VCol>
            <VCol cols="12">
              <VTextField
                name="address1"
                v-capitalize
                v-model="formData.claimant.address1"
                label="Address 1"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                name="address2"
                v-capitalize
                v-model="formData.claimant.address2"
                label="Address 2"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="city"
                v-capitalize
                v-model="formData.claimant.city"
                label="City"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol v-if="hasStateClaimantOptions" cols="12" sm="6">
              <VSelect
                name="state"
                variant="outlined"
                density="comfortable"
                color="primary"
                label="State"
                v-model="formData.claimant.state"
                :items="stateClaimantOptions"
                :disabled="isLoadingSave || !formData.claimant.country"
                :rules="[rules.required]"
              />
            </VCol>
            <!-- Claimant Address Verification -->
            <VCol cols="12">
              <div class="d-flex align-center gap-2 flex-wrap">
                <VBtn
                  color="teal"
                  size="small"
                  :loading="isValidatingAddress"
                  @click="verifyClaimantAddress(false)"
                  :disabled="!formData.claimant.address1 || !formData.claimant.city"
                >
                  Verify Address
                </VBtn>
                <VChip :color="getVerificationColor(formData.claimant)" size="small" class="address-validation-chip">
                  <VIcon size="small" left>{{ getVerificationIcon(formData.claimant) }}</VIcon>
                  {{ getVerificationStatus(formData.claimant) }}
                </VChip>
                <span v-if="formData.claimant.isPartial" class="text-warning text-caption">
                  <VIcon size="small" color="warning">mdi-alert-circle</VIcon>
                  Partially verified
                </span>
                <span v-if="isValidatingAddress" class="text-caption text-grey">
                  <VProgressCircular indeterminate size="16" width="2" class="mr-1" />
                  Validating...
                </span>
              </div>
            </VCol>

            <!-- UK Residence -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12">
              <div class="field-label">Ever Live in the UK?</div>
              <VRow align="center">
                <VCol cols="12">
                  <VBtnToggle
                    v-model="formData.claimant.everLivedUK"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12" sm="6">
                  <VDateInput
                    name="livedUKFrom"
                    v-model="formData.claimant.livedUKFrom"
                    label="From"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12" sm="6">
                  <VDateInput
                    name="livedUKTo"
                    v-model="formData.claimant.livedUKTo"
                    label="To"
                    variant="outlined"
                    :rules="[
                      rules.required,
                      (v) =>
                        !formData.claimant.livedUKFrom ||
                        !v ||
                        v >= formData.claimant.livedUKFrom ||
                        'End date must be after start date.'
                    ]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12">
                  <VSelect
                    name="approxYearsInUK"
                    v-model="formData.claimant.approxYearsInUK"
                    label="Approx Years in UK?"
                    :items="approxYearsInUKptions"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12">
                  <VTextarea v-model="formData.claimant.everLivedUKNotes" :disabled="isLoadingSave"></VTextarea>
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12">
              <div class="field-label">Ever Work in the UK?</div>
              <VRow align="center">
                <VCol cols="12">
                  <VBtnToggle
                    v-model="formData.claimant.everWorkedUK"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </VCol>
                <VCol v-if="formData.claimant.everWorkedUK === true" cols="12">
                  <VBtnToggle
                    v-model="formData.claimant.everWorkedUKuseSameDates"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Use Same Dates</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">Use Other Dates</VBtn>
                  </VBtnToggle>
                </VCol>
                <VCol
                  v-if="formData.claimant.everWorkedUK === true && !formData.claimant.everWorkedUKuseSameDates"
                  cols="12"
                  sm="6"
                >
                  <VDateInput
                    name="workedUKFrom"
                    v-model="formData.claimant.workedUKFrom"
                    label="From"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol
                  v-if="formData.claimant.everWorkedUK === true && !formData.claimant.everWorkedUKuseSameDates"
                  cols="12"
                  sm="6"
                >
                  <VDateInput
                    name="workedUKTo"
                    v-model="formData.claimant.workedUKTo"
                    label="To"
                    variant="outlined"
                    :rules="[
                      rules.required,
                      (v) =>
                        !formData.claimant.workedUKFrom ||
                        !v ||
                        v >= formData.claimant.workedUKFrom ||
                        'End date must be after start date.'
                    ]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12" v-if="formData.claimant.everWorkedUK === true">
              <VSelect
                name="hasProofOfUKWork"
                v-model="formData.claimant.hasProofOfUKWork"
                label="Have Proof of UK Work?"
                :items="[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' }
                ]"
                item-title="label"
                item-value="value"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>

            <!-- Countries Lived -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12" v-for="(country, ci) in formData.claimant.countriesLived" :key="'cl' + ci">
              <div class="mb-2 mt-2">
                <div class="d-flex justify-space-between align-center mb-3">
                  <span class="field-label mb-4">Which (NON-UK) Country have you Lived in ({{ ci + 1 }})</span>
                  <VBtn
                    v-if="formData.claimant.countriesLived.length > 1 && ci != 0"
                    icon="mdi-close"
                    variant="text"
                    @click="removeCountry(ci)"
                    :disabled="isLoadingSave"
                  />
                </div>
                <VRow>
                  <VCol cols="12" sm="6">
                    <VSelect
                      v-model="country.country"
                      label="Country"
                      :items="countryOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="country.socialSecurityNumber"
                      label="Social Security Number There"
                      placeholder="e.g. 1234567"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12"><div class="field-label">Do you live here NOW?</div></VCol>
                  <VCol cols="12">
                    <VBtnToggle v-model="country.isLiveHereNow" mandatory color="teal" rounded="lg" border>
                      <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                      <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                    </VBtnToggle>
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VDateInput
                      v-model="country.fromDate"
                      label="From"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol v-if="!country.isLiveHereNow" cols="12" sm="6">
                    <VDateInput
                      v-model="country.toDate"
                      label="To"
                      variant="outlined"
                      density="compact"
                      :rules="[
                        rules.required,
                        (v) => !country.fromDate || !v || v >= country.fromDate || 'End date must be after start date.'
                      ]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                </VRow>
              </div>
            </VCol>
            <VCol cols="12">
              <VBtn
                variant="outlined"
                color="teal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="addCountry"
                :disabled="isLoadingSave"
              >
                Add Country
              </VBtn>
            </VCol>

            <!-- If Married/Widowed -->
            <template v-if="!['Single'].includes(formData.claimant.maritalStatus) && formData.claimant.title !== 'Mr'">
              <VCol cols="12"><div class="section-divider-label"></div></VCol>
              <VCol cols="12"><div class="field-label">If Married / Widowed</div></VCol>
              <VCol cols="12">
                <div class="toggle-row mb-3">
                  <span class="field-label">Ever Paid Married Woman's Stamp?</span>
                  <VBtnToggle
                    v-model="formData.claimant.marriedWomansStamp"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </div>
                <VCol cols="12"><div class="section-divider-label"></div></VCol>
                <div class="toggle-row mb-3">
                  <span class="field-label">Minded Kids in UK 1978-2010?</span>
                  <VBtnToggle
                    v-model="formData.claimant.mindedKids"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </div>
              </VCol>

              <!-- Children -->
              <template v-if="formData.claimant.mindedKids === true">
                <VCol cols="12" v-for="(child, chi) in formData.claimant.children" :key="'ch' + chi">
                  <div class="mb-2">
                    <div class="d-flex justify-space-between align-center mb-3">
                      <span class="field-label mb-4">Child ({{ chi + 1 }})</span>
                      <VBtn icon="mdi-close" variant="text" @click="removeChild(chi)" :disabled="isLoadingSave" />
                    </div>
                    <VRow>
                      <VCol cols="12" sm="6">
                        <VTextField
                          v-capitalize
                          v-model="child.name"
                          label="Child's Name"
                          placeholder="e.g. Name of the kid"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.required]"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12" sm="6">
                        <VDateInput
                          v-model="child.mindedFromDate"
                          label="Minded at Home From"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.required]"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          input-format="dd/mm/yyyy"
                          v-auto-slash
                          :max="dateToday"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12" sm="6">
                        <VDateInput
                          v-model="child.dob"
                          label="Child's Date of Birth"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.required]"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          input-format="dd/mm/yyyy"
                          v-auto-slash
                          :max="maxDate"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12" sm="6">
                        <VDateInput
                          v-model="child.mindedToDate"
                          label="Minded at Home To"
                          variant="outlined"
                          density="compact"
                          :rules="[
                            rules.required,
                            (v) =>
                              !child.mindedFromDate ||
                              !v ||
                              v >= child.mindedFromDate ||
                              'End date must be after start date.'
                          ]"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          input-format="dd/mm/yyyy"
                          v-auto-slash
                          :max="dateToday"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12">
                        <VTextField
                          v-capitalize
                          v-model="child.nino"
                          label="Child's National Insurance Number (if known)"
                          placeholder="e.g. NX123456B"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.nino]"
                          :disabled="isLoadingSave"
                          name="nino"
                        />
                      </VCol>
                    </VRow>
                  </div>
                </VCol>
                <VCol cols="12">
                  <VBtn
                    variant="outlined"
                    color="teal"
                    prepend-icon="mdi-plus"
                    rounded="lg"
                    @click="addChild"
                    :disabled="isLoadingSave"
                  >
                    Add Child
                  </VBtn>
                </VCol>
              </template>
            </template>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 3 ──────────────── -->
        <VForm v-if="currentStep === 3" ref="form3" v-model="valid[3]" lazy-validation>
          <h2 class="step-title mb-6">3. About your husband, wife or civil partner</h2>

          <VRow>
            <VCol cols="12" sm="4">
              <VSelect
                v-model="formData.partner.title"
                label="Title"
                :items="titleOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :readonly="formData.claimant.maritalStatus === 'Single'"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              />
            </VCol>
            <VCol cols="12" sm="4">
              <VTextField
                v-capitalize
                v-model="formData.partner.firstName"
                label="First Name"
                placeholder="Eg: Mary"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              />
            </VCol>
            <VCol cols="12" sm="4">
              <VTextField
                v-capitalize
                v-model="formData.partner.surname"
                label="Surname / Family name"
                placeholder="e.g. Doe"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.partner.otherNames"
                label="Middle Name(s)"
                placeholder="Eg: Catherine"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VDateInput
                v-model="formData.partner.dob"
                label="Date of Birth"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="maxDate"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.partner.maidenName"
                label="Maiden/Other/Previous Names "
                placeholder="Full maiden / former name(s)"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              />
            </VCol>

            <VCol cols="12">
              <div class="field-label">Claiming UK Pension</div>
              <VBtnToggle
                v-model="formData.partner.claimingUKPension"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn
                  :value="true"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >Yes</VBtn
                >
                <VBtn
                  :value="false"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >No</VBtn
                >
              </VBtnToggle>
            </VCol>

            <!-- UK NINO -->
            <VCol cols="12">
              <div class="field-label">“Ever Had a UK Nat Ins No”?</div>
              <VBtnToggle v-model="formData.partner.hasNino" mandatory color="teal" rounded="lg" border class="mt-4">
                <VBtn
                  :value="true"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >Yes</VBtn
                >
                <VBtn
                  :value="false"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >No</VBtn
                >
              </VBtnToggle>
            </VCol>
            <VCol cols="12" v-if="formData.partner.hasNino === true">
              <VTextField
                v-capitalize
                v-model="formData.partner.nino"
                label="National Insurance Number"
                placeholder="e.g. NX123456B"
                variant="outlined"
                :rules="[rules.required, rules.nino]"
                density="comfortable"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                name="nino"
              />
            </VCol>

            <VCol cols="12">
              <div class="field-label">Ever Live in the UK?</div>
              <VBtnToggle
                v-model="formData.partner.everLivedUK"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn
                  :value="true"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >Yes</VBtn
                >
                <VBtn
                  :value="false"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >No</VBtn
                >
              </VBtnToggle>
            </VCol>
            <VCol v-if="formData.partner.everLivedUK === true" cols="12">
              <VTextarea
                v-model="formData.partner.everLivedUKNotes"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              ></VTextarea>
            </VCol>
            <VCol cols="12">
              <div class="field-label">Ever Work in the UK?</div>
              <VBtnToggle
                v-model="formData.partner.everWorkedUK"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn
                  :value="true"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >Yes</VBtn
                >
                <VBtn
                  :value="false"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >No</VBtn
                >
              </VBtnToggle>
            </VCol>
            <VCol cols="12">
              <VTextarea
                label="Reference on Any DWP Letters?"
                v-model="formData.partner.referenceOnAnyDWPLetters"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              ></VTextarea>
            </VCol>
            <!-- Address -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12">
              <div class="field-label">Is Their Address Same as Claimant?</div>
              <VBtnToggle
                v-model="formData.partner.saveAsClaimant"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn
                  :value="true"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >Yes</VBtn
                >
                <VBtn
                  :value="false"
                  size="small"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                  >No</VBtn
                >
              </VBtnToggle>
            </VCol>
            <template v-if="!formData.partner.saveAsClaimant">
              <VCol cols="12" sm="6">
                <VSelect
                  v-model="formData.partner.country"
                  label="Country"
                  :items="countryOptions"
                  item-title="label"
                  item-value="value"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.postcode"
                  label="Postcode"
                  placeholder="e.g. F28 AE22"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                />
              </VCol>
              <VCol v-if="formData.partner.country" cols="12">
                <AddressSearch
                  :country="formData.partner.country || 'GBR'"
                  @address-selected="onPartnerAddressSelected"
                >
                </AddressSearch>
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.address1"
                  label="Address 1"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.address2"
                  label="Address 2"
                  variant="outlined"
                  density="comfortable"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.city"
                  label="City"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                />
              </VCol>
              <VCol v-if="hasStatePartnerOptions" cols="12" sm="6">
                <VSelect
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  name="state"
                  label="State"
                  v-model="formData.partner.state"
                  :items="statePartnerOptions"
                  :disabled="isLoadingSave || !formData.partner.country || formData.claimant.maritalStatus === 'Single'"
                  :rules="[rules.required]"
                />
              </VCol>
              <!-- Partner Address Verification -->
              <VCol cols="12">
                <div class="d-flex align-center gap-2 flex-wrap">
                  <VBtn
                    color="teal"
                    size="small"
                    :loading="isValidatingAddress"
                    @click="verifyPartnerAddress(false)"
                    :disabled="!formData.partner.address1 || !formData.partner.city"
                  >
                    Verify Address
                  </VBtn>
                  <VChip :color="getVerificationColor(formData.partner)" size="x-small" class="address-validation-chip">
                    <VIcon size="x-small" left>{{ getVerificationIcon(formData.partner) }}</VIcon>
                    {{ getVerificationStatus(formData.partner) }}
                  </VChip>
                  <span v-if="formData.partner.wasCleansed" class="text-warning text-caption">
                    <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                    Corrected
                  </span>
                  <span v-if="isValidatingAddress" class="text-caption text-grey">
                    <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                    Validating...
                  </span>
                </div>
              </VCol>
            </template>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 4 ──────────────── -->
        <VForm v-if="currentStep === 4" ref="form4" v-model="valid[4]" lazy-validation>
          <h2 class="step-title mb-6">4. Have you (Claimant) Ever Claimed:</h2>

          <VRow>
            <VCol cols="12" v-for="benefit in benefitTypes" :key="benefit.key">
              <div class="toggle-row mb-3">
                <span class="field-label">
                  {{ benefit.label }}
                </span>
                <VBtnToggle
                  v-model="formData.claimantBenefits[benefit.key]"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </div>
            </VCol>
            <VCol cols="12" v-if="formData.claimantBenefits.isWaitingUkBenefits">
              <VTextarea
                v-model="formData.claimantBenefits.isWaitingUkBenefitsText"
                :disabled="isLoadingSave"
              ></VTextarea>
            </VCol>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 5 ──────────────── -->
        <VForm v-if="currentStep === 5" ref="form5" v-model="valid[5]" lazy-validation>
          <h2 class="step-title mb-6">5. Has Partner Ever Claimed</h2>

          <VRow>
            <VCol cols="12" v-for="benefit in benefitTypesForPartner" :key="benefit.key">
              <div class="toggle-row mb-3">
                <span class="field-label">
                  {{ benefit.label }}
                </span>
                <VBtnToggle
                  v-model="formData.partnerBenefits[benefit.key]"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn
                    :value="true"
                    size="small"
                    :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                    >Yes</VBtn
                  >
                  <VBtn
                    :value="false"
                    size="small"
                    :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
                    >No</VBtn
                  >
                </VBtnToggle>
              </div>
            </VCol>
            <VCol cols="12" v-if="formData.partnerBenefits.isWaitingUkBenefits">
              <VTextarea
                v-model="formData.partnerBenefits.isWaitingUkBenefitsText"
                :disabled="isLoadingSave || formData.claimant.maritalStatus === 'Single'"
              ></VTextarea>
            </VCol>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 6 ──────────────── -->
        <VForm v-if="currentStep === 6" ref="form6" v-model="valid[6]" lazy-validation>
          <h2 class="step-title mb-6">6. Are You Claiming Pension from Today?</h2>

          <VRow>
            <VCol cols="12">
              <VBtnToggle
                v-model="formData.pension.claimingFromToday"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <VCol cols="12" v-if="formData.pension.claimingFromToday === false">
              <VDateInput
                v-model="formData.pension.startDate"
                label="When will you start claiming?"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :min="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>

            <VCol cols="12">
              <p class="field-label mt-3">
                Do you intend to work for an employer after you have reached state pension age?
              </p>
              <VBtnToggle
                v-model="formData.pension.workAfterPensionAge"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <!-- <VCol cols="12">
              <p class="field-label mt-3">Have you already put off getting your State Pension?</p>
              <VBtnToggle
                v-model="formData.pension.deferredPension"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol> -->

            <VCol cols="12">
              <p class="field-label mt-3">
                If you have already reached state pension age, have you spent any time in prison since then?
              </p>
              <VBtnToggle
                v-model="formData.pension.imprisonedSincePensionAge"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <VCol cols="12">
              <p class="field-label mt-3">
                Did you do any work in the UK during the 2 years just before you reached the UK State Pension age?
              </p>
              <VBtnToggle
                v-model="formData.pension.workInUKduring2years"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <template
              v-if="
                ['Ms', 'Miss'].includes(formData.claimant.title) &&
                ['Married', 'Civil Partner', 'Divorced', 'Separated'].includes(formData.claimant.maritalStatus) &&
                formData.claimant.everWorkedUK
              "
            >
              <VCol cols="12">
                <p class="field-label mt-3">Were you ever self-employed in the UK after 1978?</p>
                <VBtnToggle
                  v-model="formData.pension.everSelfEmployedUKafter1978"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>
              <VCol cols="12">
                <p class="field-label mt-3">
                  Did you ever pay reduced UK National Insurance (commonly known as the "married woman's stamp")?
                </p>
                <VBtnToggle
                  v-model="formData.pension.everPayReducedUKnino"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>
            </template>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 7 ──────────────── -->
        <VForm v-if="currentStep === 7" ref="form7" v-model="valid[7]" lazy-validation>
          <h2 class="step-title mb-6">7. Current Pension / Benefits</h2>

          <VRow>
            <VCol cols="12">
              <div class="field-label">
                {{
                  formData.claimant.maritalStatus === 'Single'
                    ? 'Do YOU currently get a pension or other benefit where you live now?'
                    : 'Do YOU or your PARTNER currently get a pension or other benefit where you live now?'
                }}
              </div>
              <VBtnToggle
                v-model="formData.benefits.isYourPartnerCurrentlyGetPension"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <VCol cols="12" v-if="formData.benefits.isYourPartnerCurrentlyGetPension">
              <VTextarea
                v-model="formData.benefits.isYourPartnerCurrentlyGetPensionText"
                label="What is it?"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
                placeholder="eg: Contrib SP or Non-Contrib SP (Irl) / Age Pension (Oz)"
              />
            </VCol>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 8 ──────────────── -->
        <VForm v-if="currentStep === 8" ref="form8" v-model="valid[8]" lazy-validation>
          <h2 class="step-title mb-6">8. Bank Details</h2>
          <VTextField
            v-capitalize
            v-model="formData.bank.accountOwnerName"
            label="Account Owner Name (Match bank statement!)"
            placeholder="John Doe"
            variant="outlined"
            :rules="[rules.required]"
            density="comfortable"
            class="mb-2"
            :disabled="isLoadingSave"
          />
          <VSelect
            v-model="formData.bank.ownsThisAccount"
            label="Who Owns This Account"
            :items="bankOwnsThisAccountOptions"
            item-title="label"
            item-value="value"
            variant="outlined"
            :rules="[rules.required]"
            density="comfortable"
            :disabled="isLoadingSave"
          />
          <VTextField
            v-if="formData.bank.ownsThisAccount && formData.bank.ownsThisAccount !== 'Claimant'"
            v-model="formData.bank.accountOwnerEmail"
            label="Account Owner Email"
            placeholder="john.doe@example.com"
            type="email"
            variant="outlined"
            :rules="[rules.required, rules.email]"
            density="comfortable"
            class="mt-2"
            :disabled="isLoadingSave"
          />
          <VTextField
            v-if="shouldShowIban(formData.bank.country)"
            v-capitalize
            v-model="formData.bank.iban"
            label="IBAN"
            placeholder="Main thing for IRL/EU"
            variant="outlined"
            :rules="[rules.required, rules.iban]"
            density="comfortable"
            class="mb-2"
            :disabled="isLoadingSave || isLoadingBankInfo"
          />
          <div class="my-5">
            <VBtn
              variant="outlined"
              color="teal"
              rounded="lg"
              size="large"
              @click="verifyBank()"
              :loading="isLoadingBankInfo || isLoadingBankCity"
              :disabled="isLoadingSave || isLoadingBankInfo"
            >
              Get Bank Info
            </VBtn>
          </div>
          <VTextField
            v-if="shouldShowBic(formData.bank.country)"
            v-capitalize
            v-model="formData.bank.bic"
            label="BIC/SWIFT"
            :placeholder="formData.bank.country === 'AU' ? 'Optional - leave empty if unknown' : 'e.g., DEUTDEFF500'"
            variant="outlined"
            :rules="formData.bank.country === 'AU' ? [rules.bic] : [rules.required, rules.bic]"
            density="comfortable"
            class="mb-2"
            :disabled="isLoadingSave || isLoadingBankInfo"
          />
          <VDivider :thickness="1" class="border-opacity-25 mb-5 mt-5" />
          <VRow>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.bank.account"
                label="Account"
                placeholder="e.g. 12345678"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.bank.sortCode"
                label="Sort Code / BSB (Bank State Branch) Number"
                :placeholder="formData.bank.country === 'AU' ? 'e.g. 123-456' : 'e.g. 12-34-56'"
                variant="outlined"
                :rules="[rules.required, rules.sortCode]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo"
                @input="handleSortCodeChange"
              />
            </VCol>
          </VRow>
          <VDivider :thickness="1" class="border-opacity-25 mb-5 mt-5" />
          <VRow>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.bank.bankName"
                label="Bank Name"
                placeholder="e.g. AIB (Allied Irish Banks)"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.bank.city"
                label="Bank City"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.bank.address"
                label="Bank Address"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VSelect
                v-model="formData.bank.country"
                label="Bank Country"
                :items="countryOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.bank.postcode"
                label="Bank Postcode"
                placeholder="e.g. F28 AE22"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
          </VRow>
        </VForm>

        <!-- ──────────────── STEP 9 ──────────────── -->
        <VForm v-if="currentStep === 9" ref="form9" v-model="valid[9]" lazy-validation>
          <h2 class="text-center step-title mb-6">Summary</h2>
          <VDivider class="my-6" />
          <h2 class="step-title mb-6">Claim-on-Husband</h2>
          <VRow>
            <VCol cols="12" sm="6">
              <div><b>Claimant:</b> {{ formData.claimant.otherNames }}</div>
              <div>
                <b>Helper:</b> {{ formData.contactCommunication.firstName }}
                {{ formData.contactCommunication.lastName }}
              </div>
              <div><b>Has NINO:</b> {{ formData.claimant.hasNino ? 'Yes' : 'No' }}</div>
              <div><b>Lived in UK:</b> {{ formData.claimant.everLivedUK ? 'Yes' : 'No' }}</div>
              <div><b>Worked in UK:</b> {{ formData.claimant.everWorkedUK ? 'Yes' : 'No' }}</div>
            </VCol>
            <VCol v-if="formData.claimant.maritalStatus !== 'Single'" cols="12" sm="6">
              <div><b>Partner:</b> {{ formData.partner.firstName + ' ' + formData.partner.surname }}</div>
              <div><b>Getting Pension:</b> {{ formData.partnerBenefits.statePension ? 'Yes' : 'No' }}</div>
              <div><b>Has NINO:</b> {{ formData.partner.hasNino ? 'Yes' : 'No' }}</div>
              <div><b>Lived in UK:</b> {{ formData.partner.everLivedUK ? 'Yes' : 'No' }}</div>
              <div><b>Worked in UK:</b> {{ formData.partner.everWorkedUK ? 'Yes' : 'No' }}</div>
            </VCol>
          </VRow>
          <VDivider class="my-6" />
          <h2 class="step-title mb-6">1. Friend / Family for Communication</h2>
          <VRow>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.firstName"
                label="First name"
                placeholder="e.g. John"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.lastName"
                label="Last name"
                placeholder="e.g. Doe"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.tel"
                label="Tel"
                placeholder="e.g. (083) 8112345"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-model="formData.contactCommunication.email"
                label="Email"
                type="email"
                placeholder="e.g. name@example.com"
                variant="outlined"
                :rules="[rules.agentRequired, rules.email]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.contactCommunication.relationship"
                label="Relationship to Claimant"
                placeholder="e.g. Daughter / Friend"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
                @input="handleRelationshipChange"
              />
            </VCol>
          </VRow>
          <VDivider class="my-6" />
          <h2 class="step-title mb-6">2. About the Claimant</h2>
          <VRow>
            <VCol cols="12" sm="4">
              <VSelect
                v-model="formData.claimant.title"
                label="Title"
                :items="titleOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="8">
              <VTextField
                v-capitalize
                v-model="formData.claimant.firstName"
                label="First Name"
                placeholder="Eg: Mary"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.surname"
                label="Surname / Family Name"
                placeholder="e.g. Doe"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="middleName"
                v-capitalize
                v-model="formData.claimant.middleName"
                label="Middle Name(s)"
                placeholder="eg. Walsh"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.maidenName"
                label="Maiden Names"
                placeholder="Full maiden name(s)"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.otherNames"
                label="Other/Previous Names "
                placeholder="Other/Previous name(s)"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VDateInput
                v-model="formData.claimant.dob"
                label="Date of Birth"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="maxDate"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" v-if="newStatePensionWarning">
              <VAlert type="warning" variant="tonal" density="comfortable">{{ newStatePensionWarning }}</VAlert>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.tel"
                label="Tel"
                placeholder="e.g. (083) 8112345"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-model="formData.claimant.email"
                label="Email"
                type="email"
                placeholder="e.g. name@example.com"
                variant="outlined"
                :rules="[rules.email]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VSelect
                v-model="formData.claimant.maritalStatus"
                label="Marital Status"
                :items="maritalStatusOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.nationality"
                label="Nationality"
                placeholder="e.g. Which passport have you?"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>

            <VCol cols="12" sm="12" v-if="['Married', 'Civil Partner'].includes(formData.claimant.maritalStatus)">
              <VDateInput
                v-model="formData.claimant.dateOfMarriage"
                label="Date of Marriage"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="12" v-if="formData.claimant.maritalStatus === 'Widowed'">
              <VDateInput
                v-model="formData.claimant.dateOfBereavement"
                label="Date of Bereavement"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="12" v-if="['Divorced', 'Separated'].includes(formData.claimant.maritalStatus)">
              <VDateInput
                v-model="formData.claimant.dateOfDivorce"
                label="Date of Divorce / Civil Dissolution"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>

            <!-- UK NINO -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12">
              <div class="field-label">“Ever Had a UK Nat Ins No”?</div>
              <VBtnToggle v-model="formData.claimant.hasNino" mandatory color="teal" rounded="lg" border class="mt-4">
                <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>
            <VCol cols="12" v-if="formData.claimant.hasNino === true">
              <VTextField
                v-capitalize
                v-model="formData.claimant.nino"
                label="National Insurance Number"
                placeholder="e.g. NX123456B"
                variant="outlined"
                :rules="[rules.agentRequired, rules.nino]"
                density="comfortable"
                :disabled="isLoadingSave"
                name="nino"
              />
            </VCol>

            <!-- Address -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12"><div class="field-label">Address</div></VCol>
            <VCol cols="12" sm="6">
              <VSelect
                v-model="formData.claimant.country"
                label="Country"
                :items="countryOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.postcode"
                label="Postcode"
                placeholder="e.g. F28 AE22"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol v-if="formData.claimant.country" cols="12">
              <AddressSearch
                :country="formData.claimant.country || 'GBR'"
                @address-selected="onClaimantAddressSelected"
              >
              </AddressSearch>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.address1"
                label="Address 1"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.address2"
                label="Address 2"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.claimant.city"
                label="City"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol v-if="hasStateClaimantOptions" cols="12" sm="6">
              <VSelect
                variant="outlined"
                density="comfortable"
                color="primary"
                name="state"
                label="State"
                v-model="formData.claimant.state"
                :items="stateClaimantOptions"
                :disabled="isLoadingSave || !formData.claimant.country"
                :rules="[rules.agentRequired]"
              />
            </VCol>
            <!-- Claimant Address Verification -->
            <VCol cols="12">
              <div class="d-flex align-center gap-2 flex-wrap">
                <VBtn
                  color="teal"
                  size="small"
                  :loading="isValidatingAddress"
                  @click="verifyClaimantAddress(false)"
                  :disabled="!formData.claimant.address1 || !formData.claimant.city"
                >
                  Verify Address
                </VBtn>
                <VChip :color="getVerificationColor(formData.claimant)" size="small" class="address-validation-chip">
                  <VIcon size="small" left>{{ getVerificationIcon(formData.claimant) }}</VIcon>
                  {{ getVerificationStatus(formData.claimant) }}
                </VChip>
                <span v-if="formData.claimant.isPartial" class="text-warning text-caption">
                  <VIcon size="small" color="warning">mdi-alert-circle</VIcon>
                  Partially verified
                </span>
                <span v-if="isValidatingAddress" class="text-caption text-grey">
                  <VProgressCircular indeterminate size="16" width="2" class="mr-1" />
                  Validating...
                </span>
              </div>
            </VCol>

            <!-- UK Residence -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12">
              <div class="field-label">Ever Live in the UK?</div>
              <VRow align="center">
                <VCol cols="12">
                  <VBtnToggle
                    v-model="formData.claimant.everLivedUK"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12" sm="6">
                  <VDateInput
                    v-model="formData.claimant.livedUKFrom"
                    label="From"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12" sm="6">
                  <VDateInput
                    v-model="formData.claimant.livedUKTo"
                    label="To"
                    variant="outlined"
                    :rules="[
                      rules.agentRequired,
                      (v) =>
                        !formData.claimant.livedUKFrom ||
                        !v ||
                        v >= formData.claimant.livedUKFrom ||
                        'End date must be after start date.'
                    ]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12">
                  <VSelect
                    v-model="formData.claimant.approxYearsInUK"
                    label="Approx Years in UK?"
                    :items="approxYearsInUKptions"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="formData.claimant.everLivedUK === true" cols="12">
                  <VTextarea v-model="formData.claimant.everLivedUKNotes" :disabled="isLoadingSave"></VTextarea>
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12">
              <div class="field-label">Ever Work in the UK?</div>
              <VRow align="center">
                <VCol cols="12">
                  <VBtnToggle
                    v-model="formData.claimant.everWorkedUK"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </VCol>
                <VCol v-if="formData.claimant.everWorkedUK === true" cols="12">
                  <VBtnToggle
                    v-model="formData.claimant.everWorkedUKuseSameDates"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Use Same Dates</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">Use Other Dates</VBtn>
                  </VBtnToggle>
                </VCol>
                <VCol
                  v-if="formData.claimant.everWorkedUK === true && !formData.claimant.everWorkedUKuseSameDates"
                  cols="12"
                  sm="6"
                >
                  <VDateInput
                    v-model="formData.claimant.workedUKFrom"
                    label="From"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol
                  v-if="formData.claimant.everWorkedUK === true && !formData.claimant.everWorkedUKuseSameDates"
                  cols="12"
                  sm="6"
                >
                  <VDateInput
                    v-model="formData.claimant.workedUKTo"
                    label="To"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12" v-if="formData.claimant.everWorkedUK === true">
              <VSelect
                v-model="formData.claimant.hasProofOfUKWork"
                label="Have Proof of UK Work?"
                :items="[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' }
                ]"
                item-title="label"
                item-value="value"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>

            <!-- Countries Lived -->
            <VCol cols="12"><div class="section-divider-label"></div></VCol>
            <VCol cols="12" v-for="(country, ci) in formData.claimant.countriesLived" :key="'cl' + ci">
              <div class="mb-2 mt-2">
                <div class="d-flex justify-space-between align-center mb-3">
                  <span class="field-label mb-4">Which (NON-UK) Country have you Lived in ({{ ci + 1 }})</span>
                  <VBtn
                    v-if="formData.claimant.countriesLived.length > 1 && ci != 0"
                    icon="mdi-close"
                    variant="text"
                    @click="removeCountry(ci)"
                    :disabled="isLoadingSave"
                  />
                </div>
                <VRow>
                  <VCol cols="12" sm="6">
                    <VSelect
                      v-model="country.country"
                      label="Country"
                      :items="countryOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.agentRequired]"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="country.socialSecurityNumber"
                      label="Social Security Number There"
                      placeholder="e.g. 1234567"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.agentRequired]"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12"><div class="field-label">Do you live here NOW?</div></VCol>
                  <VCol cols="12">
                    <VBtnToggle v-model="country.isLiveHereNow" mandatory color="teal" rounded="lg" border>
                      <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                      <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                    </VBtnToggle>
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VDateInput
                      v-model="country.fromDate"
                      label="From"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.agentRequired]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol v-if="!country.isLiveHereNow" cols="12" sm="6">
                    <VDateInput
                      v-model="country.toDate"
                      label="To"
                      variant="outlined"
                      density="compact"
                      :rules="[
                        rules.agentRequired,
                        (v) => !country.fromDate || !v || v >= country.fromDate || 'End date must be after start date.'
                      ]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                </VRow>
              </div>
            </VCol>
            <VCol cols="12">
              <VBtn
                variant="outlined"
                color="teal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="addCountry"
                :disabled="isLoadingSave"
              >
                Add Country
              </VBtn>
            </VCol>

            <!-- If Married/Widowed -->
            <template v-if="!['Single'].includes(formData.claimant.maritalStatus) && formData.claimant.title !== 'Mr'">
              <VCol cols="12"><div class="section-divider-label"></div></VCol>
              <VCol cols="12"><div class="field-label">If Married / Widowed</div></VCol>
              <VCol cols="12">
                <div class="toggle-row mb-3">
                  <span class="field-label">Ever Paid Married Woman's Stamp?</span>
                  <VBtnToggle
                    v-model="formData.claimant.marriedWomansStamp"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </div>
                <VCol cols="12"><div class="section-divider-label"></div></VCol>
                <div class="toggle-row mb-3">
                  <span class="field-label">Minded Kids in UK 1978-2010?</span>
                  <VBtnToggle
                    v-model="formData.claimant.mindedKids"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </div>
              </VCol>

              <!-- Children -->
              <template v-if="formData.claimant.mindedKids === true">
                <VCol cols="12" v-for="(child, chi) in formData.claimant.children" :key="'ch' + chi">
                  <div class="mb-2">
                    <div class="d-flex justify-space-between align-center mb-3">
                      <span class="field-label mb-4">Child ({{ chi + 1 }})</span>
                      <VBtn icon="mdi-close" variant="text" @click="removeChild(chi)" :disabled="isLoadingSave" />
                    </div>
                    <VRow>
                      <VCol cols="12" sm="6">
                        <VTextField
                          v-capitalize
                          v-model="child.name"
                          label="Child's Name"
                          placeholder="e.g. Name of the kid"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.agentRequired]"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12" sm="6">
                        <VDateInput
                          v-model="child.mindedFromDate"
                          label="Minded at Home From"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.agentRequired]"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          input-format="dd/mm/yyyy"
                          v-auto-slash
                          :max="dateToday"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12" sm="6">
                        <VDateInput
                          v-model="child.dob"
                          label="Child's Date of Birth"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.agentRequired]"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          input-format="dd/mm/yyyy"
                          v-auto-slash
                          :max="maxDate"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12" sm="6">
                        <VDateInput
                          v-model="child.mindedToDate"
                          label="Minded at Home To"
                          variant="outlined"
                          density="compact"
                          :rules="[
                            rules.agentRequired,
                            (v) =>
                              !child.mindedFromDate ||
                              !v ||
                              v >= child.mindedFromDate ||
                              'End date must be after start date.'
                          ]"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          input-format="dd/mm/yyyy"
                          v-auto-slash
                          :max="dateToday"
                          :disabled="isLoadingSave"
                        />
                      </VCol>
                      <VCol cols="12">
                        <VTextField
                          v-capitalize
                          v-model="child.nino"
                          label="Child's National Insurance Number (if known)"
                          placeholder="e.g. NX123456B"
                          variant="outlined"
                          density="compact"
                          :rules="[rules.nino]"
                          :disabled="isLoadingSave"
                          name="nino"
                        />
                      </VCol>
                    </VRow>
                  </div>
                </VCol>
                <VCol cols="12">
                  <VBtn
                    variant="outlined"
                    color="teal"
                    prepend-icon="mdi-plus"
                    rounded="lg"
                    @click="addChild"
                    :disabled="isLoadingSave"
                  >
                    Add Child
                  </VBtn>
                </VCol>
              </template>
            </template>
          </VRow>
          <VDivider class="my-6" />
          <template v-if="formData.claimant.maritalStatus !== 'Single'">
            <h2 class="step-title mb-6">3. About your husband, wife or civil partner</h2>
            <VRow>
              <VCol cols="12" sm="4">
                <VSelect
                  v-model="formData.partner.title"
                  label="Title"
                  :items="titleOptions"
                  item-title="label"
                  item-value="value"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.firstName"
                  label="First Name"
                  placeholder="Eg: Mary"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.surname"
                  label="Surname / Family name"
                  placeholder="e.g. Doe"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.otherNames"
                  label="Middle Name(s)"
                  placeholder="Eg: Catherine"
                  variant="outlined"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VDateInput
                  v-model="formData.partner.dob"
                  label="Date of Birth"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar"
                  input-format="dd/mm/yyyy"
                  v-auto-slash
                  :max="maxDate"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.maidenName"
                  label="Maiden/Other/Previous Names "
                  placeholder="Full maiden / former name(s)"
                  variant="outlined"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>

              <VCol cols="12">
                <div class="field-label">Claiming UK Pension</div>
                <VBtnToggle
                  v-model="formData.partner.claimingUKPension"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>

              <!-- UK NINO -->
              <VCol cols="12">
                <div class="field-label">“Ever Had a UK Nat Ins No”?</div>
                <VBtnToggle v-model="formData.partner.hasNino" mandatory color="teal" rounded="lg" border class="mt-4">
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>
              <VCol cols="12" v-if="formData.partner.hasNino === true">
                <VTextField
                  v-capitalize
                  v-model="formData.partner.nino"
                  label="National Insurance Number"
                  placeholder="e.g. NX123456B"
                  variant="outlined"
                  :rules="[rules.agentRequired, rules.nino]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                  name="nino"
                />
              </VCol>

              <VCol cols="12">
                <div class="field-label">Ever Live in the UK?</div>
                <VBtnToggle
                  v-model="formData.partner.everLivedUK"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>

              <VCol v-if="formData.partner.everLivedUK === true" cols="12">
                <VTextarea v-model="formData.partner.everLivedUKNotes" :disabled="isLoadingSave"></VTextarea>
              </VCol>

              <VCol cols="12">
                <div class="field-label">Ever Work in the UK?</div>
                <VBtnToggle
                  v-model="formData.partner.everWorkedUK"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>

              <VCol cols="12">
                <VTextarea
                  label="Reference on Any DWP Letters?"
                  v-model="formData.partner.referenceOnAnyDWPLetters"
                  :disabled="isLoadingSave"
                ></VTextarea>
              </VCol>
              <!-- Address -->
              <VCol cols="12"><div class="section-divider-label"></div></VCol>
              <VCol cols="12">
                <div class="field-label">Is Their Address Same as Claimant?</div>
                <VBtnToggle
                  v-model="formData.partner.saveAsClaimant"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>
              <template v-if="!formData.partner.saveAsClaimant">
                <VCol cols="12" sm="6">
                  <VSelect
                    v-model="formData.partner.country"
                    label="Country"
                    :items="countryOptions"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-capitalize
                    v-model="formData.partner.postcode"
                    label="Postcode"
                    placeholder="e.g. F28 AE22"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="formData.partner.country" cols="12">
                  <AddressSearch
                    :country="formData.partner.country || 'GBR'"
                    @address-selected="onPartnerAddressSelected"
                  >
                  </AddressSearch>
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-capitalize
                    v-model="formData.partner.address1"
                    label="Address 1"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-capitalize
                    v-model="formData.partner.address2"
                    label="Address 2"
                    variant="outlined"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-capitalize
                    v-model="formData.partner.city"
                    label="City"
                    variant="outlined"
                    :rules="[rules.agentRequired]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol v-if="hasStatePartnerOptions" cols="12" sm="6">
                  <VSelect
                    variant="outlined"
                    density="comfortable"
                    color="primary"
                    name="state"
                    label="State"
                    v-model="formData.partner.state"
                    :items="statePartnerOptions"
                    :disabled="
                      isLoadingSave || !formData.partner.country || formData.claimant.maritalStatus === 'Single'
                    "
                    :rules="[rules.agentRequired]"
                  />
                </VCol>
                <!-- Partner Address Verification -->
                <VCol cols="12">
                  <div class="d-flex align-center gap-2 flex-wrap">
                    <VBtn
                      color="teal"
                      size="small"
                      :loading="isValidatingAddress"
                      @click="verifyPartnerAddress(false)"
                      :disabled="!formData.partner.address1 || !formData.partner.city"
                    >
                      Verify Address
                    </VBtn>
                    <VChip
                      :color="getVerificationColor(formData.partner)"
                      size="x-small"
                      class="address-validation-chip"
                    >
                      <VIcon size="x-small" left>{{ getVerificationIcon(formData.partner) }}</VIcon>
                      {{ getVerificationStatus(formData.partner) }}
                    </VChip>
                    <span v-if="formData.partner.wasCleansed" class="text-warning text-caption">
                      <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                      Corrected
                    </span>
                    <span v-if="isValidatingAddress" class="text-caption text-grey">
                      <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                      Validating...
                    </span>
                  </div>
                </VCol>
              </template>
            </VRow>
            <VDivider class="my-6" />
          </template>

          <h2 class="step-title mb-6">4. Have you (Claimant) Ever Claimed:</h2>
          <VRow>
            <VCol cols="12" v-for="benefit in benefitTypes" :key="benefit.key">
              <div class="toggle-row mb-3">
                <span class="field-label">
                  {{ benefit.label }}
                </span>
                <VBtnToggle
                  v-model="formData.claimantBenefits[benefit.key]"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </div>
            </VCol>
            <VCol cols="12" v-if="formData.claimantBenefits.isWaitingUkBenefits">
              <VTextarea
                v-model="formData.claimantBenefits.isWaitingUkBenefitsText"
                :disabled="isLoadingSave"
                :rules="[rules.agentRequired]"
              ></VTextarea>
            </VCol>
          </VRow>
          <VDivider class="my-6" />
          <template v-if="formData.claimant.maritalStatus !== 'Single'">
            <h2 class="step-title mb-6">5. Has Partner Ever Claimed</h2>
            <VRow>
              <VCol cols="12" v-for="benefit in benefitTypesForPartner" :key="benefit.key">
                <div class="toggle-row mb-3">
                  <span class="field-label">
                    {{ benefit.label }}
                  </span>
                  <VBtnToggle
                    v-model="formData.partnerBenefits[benefit.key]"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-4"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                  </VBtnToggle>
                </div>
              </VCol>
              <VCol cols="12" v-if="formData.partnerBenefits.isWaitingUkBenefits">
                <VTextarea
                  v-model="formData.partnerBenefits.isWaitingUkBenefitsText"
                  :disabled="isLoadingSave"
                  :rules="[rules.agentRequired]"
                ></VTextarea>
              </VCol>
            </VRow>
            <VDivider class="my-6" />
          </template>
          <h2 class="step-title mb-6">6. Are You Claiming Pension from Today?</h2>
          <VRow>
            <VCol cols="12">
              <VBtnToggle
                v-model="formData.pension.claimingFromToday"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <VCol cols="12" v-if="formData.pension.claimingFromToday === false">
              <VDateInput
                v-model="formData.pension.startDate"
                label="When will you start claiming?"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :min="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>

            <VCol cols="12">
              <p class="field-label mt-3">
                Do you intend to work for an employer after you have reached state pension age?
              </p>
              <VBtnToggle
                v-model="formData.pension.workAfterPensionAge"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <!-- <VCol cols="12">
              <p class="field-label mt-3">Have you already put off getting your State Pension?</p>
              <VBtnToggle
                v-model="formData.pension.deferredPension"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol> -->

            <VCol cols="12">
              <p class="field-label mt-3">
                If you have already reached state pension age, have you spent any time in prison since then?
              </p>
              <VBtnToggle
                v-model="formData.pension.imprisonedSincePensionAge"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>
            <VCol cols="12">
              <p class="field-label mt-3">
                Did you do any work in the UK during the 2 years just before you reached the UK State Pension age?
              </p>
              <VBtnToggle
                v-model="formData.pension.workInUKduring2years"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <template
              v-if="
                ['Ms', 'Miss'].includes(formData.claimant.title) &&
                ['Married', 'Civil Partner', 'Divorced', 'Separated'].includes(formData.claimant.maritalStatus) &&
                formData.claimant.everWorkedUK
              "
            >
              <VCol cols="12">
                <p class="field-label mt-3">Were you ever self-employed in the UK after 1978?</p>
                <VBtnToggle
                  v-model="formData.pension.everSelfEmployedUKafter1978"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>
              <VCol cols="12">
                <p class="field-label mt-3">
                  Did you ever pay reduced UK National Insurance (commonly known as the "married woman's stamp")?
                </p>
                <VBtnToggle
                  v-model="formData.pension.everPayReducedUKnino"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-4"
                >
                  <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>
            </template>
          </VRow>
          <VDivider class="my-6" />
          <h2 class="step-title mb-6">7. Current Pension / Benefits</h2>
          <VRow>
            <VCol cols="12">
              <div class="field-label">
                {{
                  formData.claimant.maritalStatus === 'Single'
                    ? 'Do YOU currently get a pension or other benefit where you live now?'
                    : 'Do YOU or your PARTNER currently get a pension or other benefit where you live now?'
                }}
              </div>
              <VBtnToggle
                v-model="formData.benefits.isYourPartnerCurrentlyGetPension"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <VCol cols="12" v-if="formData.benefits.isYourPartnerCurrentlyGetPension">
              <VTextarea
                v-model="formData.benefits.isYourPartnerCurrentlyGetPensionText"
                label="What is it?"
                :rules="[rules.benefitsRequired]"
                density="comfortable"
                :disabled="isLoadingSave"
                placeholder="eg: Contrib SP or Non-Contrib SP (Irl) / Age Pension (Oz)"
              />
            </VCol>
          </VRow>
          <VDivider class="my-6" />
          <h2 class="step-title mb-6">8. Bank Details</h2>
          <VTextField
            v-capitalize
            v-model="formData.bank.accountOwnerName"
            label="Account Owner Name (Match bank statement!)"
            placeholder="John Doe"
            variant="outlined"
            :rules="[rules.agentRequired]"
            density="comfortable"
            class="mb-2"
            :disabled="isLoadingSave"
          />
          <VSelect
            v-model="formData.bank.ownsThisAccount"
            label="Who Owns This Account"
            :items="bankOwnsThisAccountOptions"
            item-title="label"
            item-value="value"
            variant="outlined"
            :rules="[rules.agentRequired]"
            density="comfortable"
            :disabled="isLoadingSave"
          />
          <VTextField
            v-if="formData.bank.ownsThisAccount && formData.bank.ownsThisAccount !== 'Claimant'"
            v-model="formData.bank.accountOwnerEmail"
            label="Account Owner Email"
            placeholder="john.doe@example.com"
            type="email"
            variant="outlined"
            :rules="[rules.agentRequired, rules.email]"
            density="comfortable"
            class="mt-2"
            :disabled="isLoadingSave"
          />
          <VTextField
            v-if="shouldShowIban(formData.bank.country)"
            v-capitalize
            v-model="formData.bank.iban"
            label="IBAN"
            placeholder="Main thing for IRL/EU"
            variant="outlined"
            :rules="[rules.agentRequired, rules.iban]"
            density="comfortable"
            class="mb-2"
            :disabled="isLoadingSave || isLoadingBankInfo"
          />
          <div class="my-5">
            <VBtn
              variant="outlined"
              color="teal"
              rounded="lg"
              size="large"
              @click="verifyBank()"
              :loading="isLoadingBankInfo || isLoadingBankCity"
              :disabled="isLoadingSave || isLoadingBankInfo"
            >
              Get Bank Info
            </VBtn>
          </div>
          <VTextField
            v-if="shouldShowBic(formData.bank.country)"
            v-capitalize
            v-model="formData.bank.bic"
            label="BIC/SWIFT"
            :placeholder="formData.bank.country === 'AU' ? 'Optional - leave empty if unknown' : 'e.g., DEUTDEFF500'"
            variant="outlined"
            :rules="formData.bank.country === 'AU' ? [rules.bic] : [rules.agentRequired, rules.bic]"
            density="comfortable"
            class="mb-2"
            :disabled="isLoadingSave || isLoadingBankInfo"
          />
          <VDivider :thickness="1" class="border-opacity-25 mb-5 mt-5" />
          <VRow>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.bank.account"
                label="Account"
                placeholder="e.g. 12345678"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.bank.sortCode"
                label="Sort Code / BSB (Bank State Branch) Number"
                :placeholder="formData.bank.country === 'AU' ? 'e.g. 123-456' : 'e.g. 12-34-56'"
                variant="outlined"
                :rules="[rules.agentRequired, rules.sortCode]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo"
                @input="handleSortCodeChange"
              />
            </VCol>
          </VRow>
          <VDivider :thickness="1" class="border-opacity-25 mb-5 mt-5" />
          <VRow>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.bank.bankName"
                label="Bank Name"
                placeholder="e.g. AIB (Allied Irish Banks)"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.bank.city"
                label="Bank City"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-capitalize
                v-model="formData.bank.address"
                label="Bank Address"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VSelect
                v-model="formData.bank.country"
                label="Bank Country"
                :items="countryOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                v-capitalize
                v-model="formData.bank.postcode"
                label="Bank Postcode"
                placeholder="e.g. F28 AE22"
                variant="outlined"
                :rules="[rules.agentRequired]"
                density="comfortable"
                :disabled="isLoadingSave || isLoadingBankInfo || isLoadingBankCity"
              />
            </VCol>
          </VRow>
          <VDivider :thickness="1" class="border-opacity-25 mb-5" />
          <h2 class="step-title mb-6">Other Information</h2>
          <VRow>
            <VCol cols="12">
              <VTextarea
                v-capitalize
                v-model="formData.otherInformation.notes"
                placeholder="Enter"
                variant="outlined"
                :disabled="isLoadingSave"
              />
            </VCol>
          </VRow>
          <VDivider :thickness="1" class="border-opacity-25 mb-5" />
          <h2 class="step-title mb-6">Because of 20% Emergency tax form, we need to ask everyone</h2>
          <VRow>
            <VCol cols="12">
              <p class="field-label mt-3">Do you own / rent any UK property?</p>
              <VBtnToggle
                v-model="formData.otherInformation.isUKproperty"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>
            <template v-if="formData.otherInformation.isUKproperty">
              <VCol cols="12"><div class="section-divider-label"></div></VCol>
              <VCol cols="12" v-for="(address, ci) in formData.otherInformation.UkProperties" :key="'uc' + ci">
                <div class="mb-2">
                  <div class="d-flex justify-space-between align-center mb-3">
                    <span class="field-label mb-4">Address ({{ ci + 1 }})</span>
                    <VBtn
                      v-if="formData.otherInformation.UkProperties.length > 1 && ci != 0"
                      icon="mdi-close"
                      variant="text"
                      @click="removeUkPropertie(ci)"
                      :disabled="isLoadingSave"
                    />
                  </div>
                  <VRow>
                    <VCol cols="12" sm="6">
                      <VSelect
                        v-model="address.country"
                        label="Country"
                        :items="countryUKOptions"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        :rules="[rules.agentRequired]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        readonly
                      />
                    </VCol>
                    <VCol cols="12" sm="6">
                      <VTextField
                        v-capitalize
                        v-model="address.postcode"
                        label="Postcode"
                        placeholder="e.g. F28 AE22"
                        variant="outlined"
                        :rules="[rules.agentRequired]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="address.address1"
                        label="Address 1"
                        variant="outlined"
                        :rules="[rules.agentRequired]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="address.address2"
                        label="Address 2"
                        variant="outlined"
                        density="comfortable"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="address.city"
                        label="City"
                        variant="outlined"
                        :rules="[rules.agentRequired]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                  </VRow>
                </div>
              </VCol>
              <VCol cols="12">
                <VBtn
                  variant="outlined"
                  color="teal"
                  prepend-icon="mdi-plus"
                  rounded="lg"
                  @click="addUkPropertie"
                  :disabled="isLoadingSave"
                >
                  Add Address
                </VBtn>
              </VCol>
              <VDivider :thickness="1" class="border-opacity-25 mb-5" />
            </template>

            <VCol cols="12">
              <p class="field-label mt-3">Do you have an accountant who takes care of your taxes?</p>
              <VBtnToggle
                v-model="formData.otherInformation.isAccountant"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>

            <template v-if="formData.otherInformation.isAccountant">
              <VCol cols="12" sm="6">
                <VTextField
                  v-capitalize
                  v-model="formData.otherInformation.accountantName"
                  label="Accountant Name"
                  placeholder="Name"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-capitalize
                  v-model="formData.otherInformation.accountantPhone"
                  label="Accountant Tel"
                  placeholder="e.g. (083) 8112345"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VSelect
                  v-model="formData.otherInformation.accountantCountry"
                  label="Accountant Country"
                  :items="countryOptions"
                  item-title="label"
                  item-value="value"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-capitalize
                  v-model="formData.otherInformation.accountantPostcode"
                  label="Accountant Postcode"
                  placeholder="e.g. F28 AE22"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-capitalize
                  v-model="formData.otherInformation.accountantAddress1"
                  label="Accountant Address 1"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-capitalize
                  v-model="formData.otherInformation.accountantAddress2"
                  label="Accountant Address 2"
                  variant="outlined"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-capitalize
                  v-model="formData.otherInformation.accountantCity"
                  label="Accountant City"
                  variant="outlined"
                  :rules="[rules.agentRequired]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VDivider :thickness="1" class="border-opacity-25 mb-5" />
            </template>

            <VCol cols="12">
              <p class="field-label mt-3">Are you self-assessed for tax?</p>
              <VBtnToggle
                v-model="formData.otherInformation.isSelfAssesedTax"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>
            <VCol v-if="formData.claimant.country == 'AU'" cols="12">
              <p class="field-label mt-3">Are you on Age Pension now / or will you be next year?</p>
              <VBtnToggle
                v-model="formData.otherInformation.isAgePension"
                mandatory
                color="teal"
                rounded="lg"
                border
                class="mt-4"
              >
                <VBtn :value="true" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>
          </VRow>
        </VForm>

        <!-- Navigation -->
        <div class="nav-row mt-8">
          <VBtn
            v-if="currentStep > 1 || (isAgentForm && currentStep === 1)"
            variant="outlined"
            color="teal"
            rounded="lg"
            size="large"
            prepend-icon="mdi-arrow-left"
            :loading="isLoadingSave"
            @click="prevStep"
          >
            Back
          </VBtn>
          <VSpacer />
          <VBtn
            v-if="currentStep < totalSteps"
            color="teal"
            rounded="lg"
            size="large"
            append-icon="mdi-arrow-right"
            :loading="isLoadingSave"
            @click="nextStep"
          >
            Continue
          </VBtn>
          <VBtn
            v-else
            color="teal"
            rounded="lg"
            size="large"
            append-icon="mdi-send"
            :loading="isLoadingSave"
            @click="submitForm"
          >
            SEND to VALIDATION
          </VBtn>
        </div>
      </div>
    </VContainer>
    <!--Form Status Submitted-->
    <VContainer v-else>
      <ThankYou>
        <template v-slot:title> <span class="text-main-color">Thanks!</span></template>
        <template v-slot:text>
          <p class="my-4">Validation Agent will now double-check all details provided.</p>
          <p class="my-4">We'll revert if any Qs.</p>
          <p class="my-4">Once done, we'll email you (and your helper) to approve the documents so we can submit.</p>
          <p class="my-4">
            A big envelope has also been sent in the post to you. Please follow the very simple instructions in the
            envelope.
          </p>
          <p class="my-4"><i>(UK need's proof of your ID and marriage/death cert for eg)</i></p>
        </template>
      </ThankYou>
    </VContainer>
  </Card>
  <!--Form Status Complete-->
  <Card v-else-if="isFormCompleted">
    <div class="py-4 text-center">
      <v-icon color="success" size="48" class="my-2">mdi-alert-circle</v-icon>
      <h1>Great! Next Steps...</h1>
      <div class="text-left">
        <p class="my-4">
          1 - Our <b>Validation team will call you</b> over the next few days once they've checked everything
        </p>
        <p class="my-4">2 - They <b>can answer any Qs</b> you have</p>
        <p class="my-4">3 - This person will be <b>your main point of contact</b> going forward</p>
        <p class="my-4">4 - Then we email YOU + HELPER for you to approve your information online. <b>(Docusign)</b></p>
        <VDivider class="my-4" />
        <p class="my-4">
          5 - <b>Sign & post</b> back what we've sent you in the post today <br />
          (We need a photocopy of your ID and marriage/death cert as applicable)
        </p>
        <p class="my-4">6 - Then we send you a <b>Royal Mail Tracking</b> ref to confirm delivery to DWP</p>
        <p class="my-4">7 - We'll be back to you <b>in approx 2 months</b> hopefully with an update</p>
        <p class="my-4">
          8 - Please <b>tell us if you get anything</b> back from DWP in the meantime.<br />
          They may call you to clarify some details so just be aware of calls from the UK.
        </p>
        <p class="my-4">9 - <b>Call us</b> if you have any Qs</p>
        <p class="my-4">10 - <b>We'll talk again</b> in a couple of months</p>
        <p class="my-4">Bye</p>
      </div>
    </div>
  </Card>
  <!--Form Not Found-->
  <Card v-else>
    <VCardItem class="pa-sm-8">
      <div class="py-4 text-center">
        <v-icon color="error" size="48" class="my-2">mdi-alert-circle</v-icon>
        <h1>Application not found!</h1>
        <p class="my-4">Failed to load application. Please check your link or try again later.</p>
      </div>
    </VCardItem>
  </Card>
</template>

<script>
import { mapActions } from 'vuex';
// Components
import { VDateInput } from 'vuetify/labs/VDateInput';
import Card from '@/components/common/Card/Card.vue';
import ThankYou from '../ThankYou/ThankYou.vue';
import AddressSearch from '../AddressSearch/AddressSearch.vue';
// Service
import ApplicationService from '@/services/application-service/application.service';
import AddressService from '@/services/address-service/address.service';
import { toLocalISODate } from '@/utils/toLocalISODate';
import GooglePlacesService from '@/services/google-places-service/google-places.service';
export default {
  name: 'FullForm',

  inject: ['countryListOptions'],

  props: {
    isAgentForm: {
      type: Boolean
    }
  },

  components: {
    ThankYou,
    VDateInput,
    Card,
    AddressSearch
  },

  mounted() {
    if (this.$route?.params?.id) {
      this.applicationId = this.$route.params.id;
      this.initForm();
    }
  },

  beforeUnmount() {
    // Clear auto-verify debounce timers
    if (this.claimantAddressDebounceTimer) {
      clearTimeout(this.claimantAddressDebounceTimer);
    }
    if (this.partnerAddressDebounceTimer) {
      clearTimeout(this.partnerAddressDebounceTimer);
    }
  },

  computed: {
    progress() {
      return ((this.currentStep - 1) / (this.totalSteps - 1)) * 100;
    },
    maxDate() {
      const today = new Date();
      const minAgeDate = new Date(today.setFullYear(today.getFullYear() - 16));
      return toLocalISODate(minAgeDate);
    },
    // The BR1 is the claim form for the old basic State Pension. Anyone reaching State Pension age on or after
    // 6 April 2016 (men born from 6 Apr 1951, women born from 6 Apr 1953) gets the new State Pension instead.
    // Only men can be told apart by title, every other title uses the later (women's) date so we never warn wrongly.
    newStatePensionWarning() {
      const dob = this.formData.claimant.dob;
      if (!dob) return '';
      const dobIso = typeof dob === 'string' ? dob.slice(0, 10) : toLocalISODate(new Date(dob));
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dobIso)) return '';
      const cutoff = this.formData.claimant.title === 'Mr' ? '1951-04-06' : '1953-04-06';
      return dobIso >= cutoff
        ? 'Based on the date of birth, this person reaches State Pension age on or after 6 April 2016, so the new State Pension applies and the BR1 claim form does not. Please check eligibility before continuing.'
        : '';
    },
    dateToday() {
      const today = new Date();
      return toLocalISODate(today);
    },
    titleOptions() {
      const options = [
        { label: 'Choose', value: '' },
        { label: 'Mr', value: 'Mr' },
        { label: 'Mrs', value: 'Mrs' },
        { label: 'Ms', value: 'Ms' },
        { label: 'Miss', value: 'Miss' },
        { label: 'Dr', value: 'Dr' },
        { label: 'Prof', value: 'Prof' }
      ];
      return options;
    },
    maritalStatusOptions() {
      const options = [
        { label: 'Choose', value: '' },
        { label: 'Single', value: 'Single' },
        { label: 'Married', value: 'Married' },
        { label: 'Widowed', value: 'Widowed' },
        { label: 'Divorced', value: 'Divorced' },
        { label: 'Civil Partner', value: 'Civil Partner' },
        { label: 'Separated', value: 'Separated' }
      ];
      return options;
    },
    approxYearsInUKptions() {
      const options = [];
      for (let i = 1; i < 31; i++) {
        options.push({
          label: i,
          value: i
        });
      }
      return options;
    },
    stateClaimantOptions() {
      const options = [{ title: 'Select County/State', value: '' }];
      const countryCode = this.formData.claimant.country?.toLowerCase();
      if (countryCode && this.countryListOptions[countryCode]?.states) {
        const states = this.countryListOptions[countryCode].states;
        for (const stateCode in states) {
          options.push({
            title: states[stateCode],
            value: states[stateCode]
          });
        }
      }
      return options;
    },
    hasStateClaimantOptions() {
      const countryCode = this.formData.claimant.country?.toLowerCase();
      return (
        countryCode &&
        this.countryListOptions[countryCode]?.states &&
        Object.keys(this.countryListOptions[countryCode].states).length > 0
      );
    },
    statePartnerOptions() {
      const options = [{ title: 'Select County/State', value: '' }];
      const countryCode = this.formData.partner.country?.toLowerCase();
      if (countryCode && this.countryListOptions[countryCode]?.states) {
        const states = this.countryListOptions[countryCode].states;
        for (const stateCode in states) {
          options.push({
            title: states[stateCode],
            value: states[stateCode]
          });
        }
      }
      return options;
    },
    hasStatePartnerOptions() {
      const countryCode = this.formData.partner.country?.toLowerCase();
      return (
        countryCode &&
        this.countryListOptions[countryCode]?.states &&
        Object.keys(this.countryListOptions[countryCode].states).length > 0
      );
    },
    countryUKOptions() {
      const options = [{ label: 'Select Country', value: '' }];
      options.push({
        label: 'United Kingdom',
        value: 'GBR'
      });

      return options;
    },
    countryOptions() {
      const options = [{ label: 'Select Country', value: '' }];

      for (const countryCode in this.countryListOptions) {
        const country = this.countryListOptions[countryCode];
        options.push({
          label: country.name,
          value: countryCode.toUpperCase()
        });
      }

      return options;
    },
    agentsOptions() {
      return [
        { label: 'Sean B', value: 'Sean B' },
        { label: 'Ciara', value: 'Ciara' },
        { label: 'Becky', value: 'Becky' },
        { label: 'MaryClaire', value: 'MaryClaire' },
        { label: 'Grace', value: 'Grace' },
        { label: 'Katherine', value: 'Katherine' }
      ];
    },
    // ─── Static lookup data ──────────────────────────
    benefitTypes() {
      return [
        { key: 'statePension', label: 'State Pension' },
        { key: 'graduatedRetirement', label: 'Graduated Retirement Benefit 1961-1975' },
        { key: 'widows', label: 'Widows Benefit' },
        { key: 'bereavement', label: 'Bereavement Benefit' },
        {
          key: 'isWaitingUkBenefits',
          label: 'Are YOU currently waiting to hear back about any UK benefits you have applied for?'
        }
      ];
    },
    benefitTypesForPartner() {
      return [
        { key: 'statePension', label: 'State Pension' },
        { key: 'graduatedRetirement', label: 'Graduated Retirement Benefit 1961-1975' },
        { key: 'widows', label: 'Widows Benefit' },
        { key: 'bereavement', label: 'Bereavement Benefit' },
        {
          key: 'isWaitingUkBenefits',
          label: 'Are THEY currently waiting to hear back about any UK benefits THEY have applied for?'
        }
      ];
    },
    bankAccountOptions() {
      return [
        { label: 'Choose', value: '' },
        { label: 'Savings Account', value: 'Savings Account' },
        { label: 'Current Account', value: 'Current Account' }
      ];
    },
    bankOwnsThisAccountOptions() {
      return [
        { label: 'Choose', value: '' },
        { label: 'Claimant', value: 'Claimant' },
        { label: 'Spouse', value: 'Spouse' },
        { label: 'Adult Child', value: 'Adult Child' },
        { label: 'Child-in-Law', value: 'Child-in-Law' },
        { label: 'Friend', value: 'Friend' },
        { label: 'Other', value: 'Other' }
      ];
    }
  },

  // ─── Data ──────────────────────────────────────────────
  data() {
    return {
      applicationId: '',
      // Stepper
      totalSteps: 9,
      currentStep: 1,
      menuItems: [
        { label: 'Helper', stepNumbers: [0, 1] },
        { label: 'Claimant Details', stepNumbers: [2] },
        { label: 'Partner Details', stepNumbers: [3] },
        { label: 'Their Claims', stepNumbers: [4] },
        { label: 'Partner Claims', stepNumbers: [5] },
        { label: 'Easy Qs', stepNumbers: [6] },
        { label: 'Benefits', stepNumbers: [7] },
        { label: 'Bank Account', stepNumbers: [8] },
        { label: 'Summary', stepNumbers: [9] }
      ],
      // Per-step form validity flags (keyed 1–9)
      valid: { 1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: true, 8: true, 9: true },

      // UI state
      isLoadingInit: true,
      lastSavedSections: {},
      isLoadingSave: false,
      isFormSuccessfullySubmited: false,
      isFormCompleted: false,
      isFormPending: false,
      isLoadingBankInfo: false,
      isLoadingBankCity: false,

      // Address validation state
      isValidatingAddress: false,

      // Auto-verify debounce timers
      claimantAddressDebounceTimer: null,
      partnerAddressDebounceTimer: null,

      // ─── Validation Rules ────────────────────────────
      rules: {
        agentRequired: (v) => !!v || 'This field is required.',
        required: (v) => {
          // Only validate if this is an agent form
          if (!this.isAgentForm) {
            return !!v || 'This field is required.';
          }
          return true;
        },
        benefitsRequired: (v) => !!v || 'Tell us which pension or benefit they get.',
        accountType: (v) => {
          if (v !== 'Current Account') {
            return 'Client needs to have a current account in order to proceed';
          }
          return true;
        },
        email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Enter a valid email address.',
        phone: (v) => !v || /^[\d\s()+\\-]{7,20}$/.test(v) || 'Enter a valid phone number.',
        nino: (v) =>
          !v ||
          /^n\/?a$/i.test((v || '').trim()) ||
          /^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z]{2}[0-9]{6}[A-D]?$/.test((v || '').replace(/\s/g, '')) ||
          'Enter a valid NI number (e.g. NX123456B) or NA.',
        sortCode: (v) => {
          if (!v) return true;
          if (this.formData.bank.country === 'AU') {
            return /^\d{3}-\d{3}$/.test(v) || 'Use format 123-456.';
          }
          return /^\d{2}-\d{2}-\d{2}$/.test(v) || 'Use format 12-34-56.';
        },
        iban: (v) => {
          const country = this.formData.bank.country;
          // If country supports IBAN, it's required
          if (this.shouldShowIban(country)) {
            if (v) {
              return /^[A-Z]{2}\d{2}[A-Z0-9]{4,32}$/i.test((v || '').replace(/\s/g, '')) || 'Enter a valid IBAN.';
            }
            return true;
          }
          return true;
        },
        bic: (v) => {
          const country = this.formData.bank.country;
          // BIC is required for IBAN countries and optional (format-checked only) for AU
          if (this.shouldShowBic(country)) {
            if (v) {
              const bicRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
              if (!bicRegex.test(v)) {
                return 'BIC must be 8 or 11 characters (letters and numbers)';
              }
              return true;
            }
            return true;
          }
          return true;
        }
      },
      // ─── Form Data ───────────────────────────────────
      formData: {
        // Step 0 – Agent
        agent: '',
        // Step 1 – Contact Communication
        contactCommunication: {
          firstName: '',
          lastName: '',
          tel: '',
          email: '',
          relationship: ''
        },

        // Step 2 – Claimant
        claimant: {
          title: '',
          surname: '',
          otherNames: '',
          dob: '',
          maidenName: '',
          middleName: '',
          tel: '',
          email: '',
          maritalStatus: '',
          nationality: '',
          dateOfMarriage: '',
          dateOfBereavement: '',
          dateOfDivorce: '',
          hasNino: false,
          nino: '',
          // Address
          country: '',
          postcode: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          // Verification status
          addressValidated: null,
          verificationStatus: null,
          wasCleansed: false,
          verifiedReference: null,
          verificationDetails: null,
          // UK history
          everLivedUK: false,
          livedUKFrom: '',
          livedUKTo: '',
          approxYearsInUK: null,
          everWorkedUK: false,
          everWorkedUKuseSameDates: false,
          workedUKFrom: '',
          workedUKTo: '',
          hasProofOfUKWork: false,
          // Dynamic lists
          countriesLived: [
            {
              country: '',
              fromDate: '',
              toDate: '',
              socialSecurityNumber: ''
            }
          ],
          ukCompanies: [
            {
              name: '',
              country: 'GBR',
              postcode: '',
              address1: '',
              address2: '',
              city: '',
              fromDate: '',
              toDate: '',
              addressValidated: null,
              verificationStatus: null,
              wasCleansed: false,
              verifiedReference: null,
              verificationDetails: null
            }
          ],
          // Married / Widowed section
          selfEmployedUK: false,
          marriedWomansStamp: false,
          mindedKids: false,
          children: []
        },

        // Step 3 - Partner
        partner: {
          title: '',
          firstName: '',
          surname: '',
          otherNames: '',
          dob: '',
          maidenName: '',
          hasNino: false,
          nino: '',
          saveAsClaimant: false,
          claimingUKPension: false,
          referenceOnAnyDWPLetters: '',
          // Address
          country: '',
          postcode: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          // Verification status
          addressValidated: null,
          verificationStatus: null,
          wasCleansed: false,
          verifiedReference: null,
          verificationDetails: null
        },

        // Step 4 – Claimant Benefits
        claimantBenefits: {
          statePension: false,
          graduatedRetirement: false,
          widows: false,
          bereavement: false
        },

        // Step 5 – Partner Benefits
        partnerBenefits: {
          statePension: false,
          graduatedRetirement: false,
          widows: false,
          bereavement: false
        },

        // Step 6 – Pension
        pension: {
          claimingFromToday: true,
          startDate: '',
          workAfterPensionAge: false,
          deferredPension: true,
          imprisonedSincePensionAge: false,

          workInUKduring2years: false,
          everSelfEmployedUKafter1978: false,
          everPayReducedUKnino: false
        },
        // Step 7 – Pension
        benefits: {
          isYourPartnerCurrentlyGetPension: false,
          isYourPartnerCurrentlyGetPensionText: ''
        },
        // Step 8 – Bank
        bank: {
          accountType: '',
          accountOwnerName: '',
          ownsThisAccount: '',
          accountOwnerEmail: '',
          bankName: '',
          sortCode: '',
          iban: '',
          bic: '',
          account: '',
          country: '',
          postcode: '',
          address: '',
          city: ''
        },
        // Other Info
        otherInformation: {
          notes: '',

          isUKproperty: false,
          UkProperties: [],

          isAccountant: false,
          accountantName: '',
          accountantPhone: '',
          accountantAddress1: '',
          accountantAddress2: '',
          accountantCity: '',
          accountantCountry: '',
          accountantPostcode: '',

          isSelfAssesedTax: false,

          isAgePension: false
        }
      }
    };
  },

  watch: {
    'formData.claimant.title': {
      handler(newVal) {
        if (newVal === 'Mr') {
          this.formData.claimant.selfEmployedUK = false;
          this.formData.claimant.marriedWomansStamp = false;
          this.formData.claimant.mindedKids = false;
        }
      }
    },
    'formData.partner.saveAsClaimant': {
      handler(newVal) {
        if (newVal) {
          // Copy address from claimant to partner
          this.formData.partner.country = this.formData.claimant.country;
          this.formData.partner.postcode = this.formData.claimant.postcode;
          this.formData.partner.address1 = this.formData.claimant.address1;
          this.formData.partner.address2 = this.formData.claimant.address2;
          this.formData.partner.city = this.formData.claimant.city;
          this.formData.partner.state = this.formData.claimant.state;
          // Copy verification status
          this.formData.partner.addressValidated = this.formData.claimant.addressValidated;
          this.formData.partner.verificationStatus = this.formData.claimant.verificationStatus;
          this.formData.partner.wasCleansed = this.formData.claimant.wasCleansed;
          this.formData.partner.verifiedReference = this.formData.claimant.verifiedReference;
          this.formData.partner.verificationDetails = this.formData.claimant.verificationDetails;
        } else {
          // Clear address fields when not same
          this.formData.partner.country = '';
          this.formData.partner.postcode = '';
          this.formData.partner.address1 = '';
          this.formData.partner.address2 = '';
          this.formData.partner.city = '';
          this.formData.partner.state = '';
          this.formData.partner.addressValidated = null;
          this.formData.partner.verificationStatus = null;
          this.formData.partner.wasCleansed = false;
          this.formData.partner.verifiedReference = null;
          this.formData.partner.verificationDetails = null;
        }
      },
      immediate: true
    },

    // ─── Reset verification when address fields change ───
    // Claimant address fields - reset verification when any field changes
    'formData.claimant.country': {
      handler() {
        this.resetClaimantVerification();
        if (this.formData.partner.saveAsClaimant) {
          this.formData.partner.country = this.formData.claimant.country;
          this.resetPartnerVerification();
        }
        this.debouncedVerifyClaimantAddress();
      }
    },
    'formData.claimant.postcode': {
      handler() {
        this.resetClaimantVerification();
        if (this.formData.partner.saveAsClaimant) {
          this.formData.partner.postcode = this.formData.claimant.postcode;
          this.resetPartnerVerification();
        }
        this.debouncedVerifyClaimantAddress();
      }
    },
    'formData.claimant.address1': {
      handler() {
        this.resetClaimantVerification();
        if (this.formData.partner.saveAsClaimant) {
          this.formData.partner.address1 = this.formData.claimant.address1;
          this.resetPartnerVerification();
        }
        this.debouncedVerifyClaimantAddress();
      }
    },
    'formData.claimant.address2': {
      handler() {
        this.resetClaimantVerification();
        if (this.formData.partner.saveAsClaimant) {
          this.formData.partner.address2 = this.formData.claimant.address2;
          this.resetPartnerVerification();
        }
        this.debouncedVerifyClaimantAddress();
      }
    },
    'formData.claimant.city': {
      handler() {
        this.resetClaimantVerification();
        if (this.formData.partner.saveAsClaimant) {
          this.formData.partner.city = this.formData.claimant.city;
          this.resetPartnerVerification();
        }
        this.debouncedVerifyClaimantAddress();
      }
    },
    'formData.claimant.state': {
      handler() {
        this.resetClaimantVerification();
        if (this.formData.partner.saveAsClaimant) {
          this.formData.partner.state = this.formData.claimant.state;
          this.resetPartnerVerification();
        }
        this.debouncedVerifyClaimantAddress();
      }
    },
    // ─── Reset partner verification when partner address changes ───
    'formData.partner.country': {
      handler() {
        if (!this.formData.partner.saveAsClaimant) {
          this.resetPartnerVerification();
          this.debouncedVerifyPartnerAddress();
        }
      }
    },
    'formData.partner.postcode': {
      handler() {
        if (!this.formData.partner.saveAsClaimant) {
          this.resetPartnerVerification();
          this.debouncedVerifyPartnerAddress();
        }
      }
    },
    'formData.partner.address1': {
      handler() {
        if (!this.formData.partner.saveAsClaimant) {
          this.resetPartnerVerification();
          this.debouncedVerifyPartnerAddress();
        }
      }
    },
    'formData.partner.address2': {
      handler() {
        if (!this.formData.partner.saveAsClaimant) {
          this.resetPartnerVerification();
          this.debouncedVerifyPartnerAddress();
        }
      }
    },
    'formData.partner.city': {
      handler() {
        if (!this.formData.partner.saveAsClaimant) {
          this.resetPartnerVerification();
          this.debouncedVerifyPartnerAddress();
        }
      }
    },
    'formData.partner.state': {
      handler() {
        if (!this.formData.partner.saveAsClaimant) {
          this.resetPartnerVerification();
          this.debouncedVerifyPartnerAddress();
        }
      }
    },
    // ─── Sync partner verification when claimant verification changes ───
    'formData.claimant.addressValidated': {
      handler(newVal) {
        if (this.formData.partner.saveAsClaimant) {
          this.formData.partner.addressValidated = newVal;
          this.formData.partner.verificationStatus = this.formData.claimant.verificationStatus;
          this.formData.partner.wasCleansed = this.formData.claimant.wasCleansed;
          this.formData.partner.verifiedReference = this.formData.claimant.verifiedReference;
          this.formData.partner.verificationDetails = this.formData.claimant.verificationDetails;
        }
      }
    },
    hasStateClaimantOptions: {
      handler(newVal) {
        if (!newVal) {
          this.formData.claimant.state = '';
        }
      }
    },
    hasStatePartnerOptions: {
      handler(newVal) {
        if (!newVal) {
          this.formData.partner.state = '';
        }
      }
    },
    // ─── Watch for boolean variables to reset dependent values ───

    // Reset NINO when hasNino becomes false
    'formData.claimant.hasNino': {
      handler(newVal) {
        if (!newVal) {
          this.formData.claimant.nino = '';
        }
      }
    },

    'formData.partner.hasNino': {
      handler(newVal) {
        if (!newVal) {
          this.formData.partner.nino = '';
        }
      }
    },

    // Reset UK residence dates when everLivedUK becomes false
    'formData.claimant.everLivedUK': {
      handler(newVal) {
        if (!newVal) {
          this.formData.claimant.livedUKFrom = '';
          this.formData.claimant.livedUKTo = '';
          this.formData.claimant.approxYearsInUK = null;
          this.formData.claimant.everLivedUKNotes = null;
        }
      }
    },
    'formData.partner.everLivedUK': {
      handler(newVal) {
        if (!newVal) {
          this.formData.partner.everLivedUKNotes = null;
        }
      }
    },
    'formData.claimant.livedUKFrom': {
      handler(newVal) {
        if (newVal && this.formData.claimant.everWorkedUKuseSameDates) {
          this.formData.claimant.workedUKFrom = this.formData.claimant.livedUKFrom;
        }
      }
    },
    'formData.claimant.livedUKTo': {
      handler(newVal) {
        if (newVal && this.formData.claimant.everWorkedUKuseSameDates) {
          this.formData.claimant.workedUKTo = this.formData.claimant.livedUKTo;
        }
      }
    },
    'formData.claimant.everWorkedUKuseSameDates': {
      handler(newVal) {
        if (newVal) {
          this.formData.claimant.workedUKFrom = this.formData.claimant.livedUKFrom;
          this.formData.claimant.workedUKTo = this.formData.claimant.livedUKTo;
        } else {
          this.formData.claimant.workedUKFrom = '';
          this.formData.claimant.workedUKTo = '';
        }
      }
    },
    // Reset UK work related fields when everWorkedUK becomes false
    'formData.claimant.everWorkedUK': {
      handler(newVal) {
        if (!newVal) {
          this.formData.claimant.workedUKFrom = '';
          this.formData.claimant.workedUKTo = '';
          this.formData.claimant.hasProofOfUKWork = false;
          this.formData.claimant.ukCompanies = [];
          this.formData.claimant.everWorkedUKuseSameDates = false;
        } else {
          this.formData.claimant.ukCompanies = [
            {
              name: '',
              country: 'GBR',
              postcode: '',
              address1: '',
              address2: '',
              city: '',
              fromDate: '',
              toDate: '',
              addressValidated: null,
              verificationStatus: null,
              wasCleansed: false,
              verifiedReference: null,
              verificationDetails: null
            }
          ];
        }
      }
    },

    // Reset children when mindedKids becomes false
    'formData.claimant.mindedKids': {
      handler(newVal) {
        if (!newVal) {
          this.formData.claimant.children = [];
        } else {
          this.addChild();
        }
      }
    },

    // Reset marital status dependent dates when status changes
    'formData.claimant.maritalStatus': {
      handler(newVal, oldVal) {
        if (oldVal && newVal !== oldVal) {
          // Reset all marital dates
          this.formData.claimant.dateOfMarriage = '';
          this.formData.claimant.dateOfBereavement = '';
          this.formData.claimant.dateOfDivorce = '';

          // Reset marriage-related flags if not applicable
          if (!['Married', 'Civil Partner', 'Widowed'].includes(newVal)) {
            this.formData.claimant.selfEmployedUK = false;
            this.formData.claimant.marriedWomansStamp = false;
            this.formData.claimant.mindedKids = false;
            this.formData.claimant.children = [];
          }
        }
      }
    },

    // Reset pension start date when claimingFromToday becomes true
    'formData.pension.claimingFromToday': {
      handler(newVal) {
        if (newVal) {
          this.formData.pension.startDate = '';
        }
      }
    },

    // Reset countriesLived when we want to clean up (optional)
    'formData.claimant.countriesLived': {
      deep: true,
      handler(newVal) {
        if (newVal && newVal.length === 0) {
          this.formData.claimant.countriesLived = [
            {
              country: '',
              fromDate: '',
              isLiveHereNow: false,
              toDate: '',
              socialSecurityNumber: ''
            }
          ];
        }
      }
    },

    'formData.claimantBenefits.isWaitingUkBenefits': {
      handler(newVal) {
        if (!newVal) {
          this.formData.claimantBenefits.isWaitingUkBenefitsText = '';
        }
      }
    },

    'formData.partnerBenefits.isWaitingUkBenefits': {
      handler(newVal) {
        if (!newVal) {
          this.formData.partnerBenefits.isWaitingUkBenefitsText = '';
        }
      }
    },

    'formData.pension.workInUKduring2years': {
      handler(newVal) {
        if (!newVal) {
          this.formData.partnerBenefits.everSelfEmployedUKafter1978 = false;
          this.formData.partnerBenefits.everPayReducedUKnino = false;
        }
      }
    },

    'formData.benefits.isYourPartnerCurrentlyGetPension': {
      handler(newVal) {
        if (!newVal) {
          this.formData.benefits.isYourPartnerCurrentlyGetPensionText = '';
        }
      }
    },

    'formData.bank.ownsThisAccount': {
      handler(newVal) {
        if (!newVal || newVal === 'Claimant') {
          this.formData.bank.accountOwnerEmail = '';
        }
      }
    },

    'formData.bank.country': {
      handler(newVal) {
        // Clear IBAN and BIC if the country doesn't support them
        if (!this.shouldShowIban(newVal)) {
          this.formData.bank.iban = '';
        }
        if (!this.shouldShowBic(newVal)) {
          this.formData.bank.bic = '';
        }
        if (newVal) {
          this.formData.bank.isEuropeanCountry = this.isEuropeanCountry(newVal);
        }
        this.formData.bank.sortCode = this.formatSortCode(this.formData.bank.sortCode, newVal);
      }
    },

    'formData.otherInformation.isUKproperty': {
      handler(newVal) {
        if (!newVal) {
          this.formData.otherInformation.UkProperties = [];
        } else {
          this.formData.otherInformation.UkProperties = [
            {
              country: 'GBR',
              postcode: '',
              address1: '',
              address2: '',
              city: ''
            }
          ];
        }
      }
    },
    'formData.otherInformation.isAccountant': {
      handler(newVal) {
        if (!newVal) {
          this.formData.otherInformation.accountantName = '';
          this.formData.otherInformation.accountantPhone = '';
          this.formData.otherInformation.accountantAddress1 = '';
          this.formData.otherInformation.accountantAddress2 = '';
          this.formData.otherInformation.accountantCity = '';
          this.formData.otherInformation.accountantCountry = '';
          this.formData.otherInformation.accountantPostcode = '';
        }
      }
    }
  },

  // ─── Methods ───────────────────────────────────────────
  methods: {
    ...mapActions('main', ['showSnackbar']),
    async initForm() {
      this.isLoadingInit = true;
      try {
        const { status, currentStep, application } = await ApplicationService.getApplicationDetails({
          applicationId: this.applicationId,
          isAgentForm: this.isAgentForm
        });
        console.log('Init Form: status, currentStep, application', status, currentStep, application);
        if (status === 'success') {
          this.isFormPending = true;
          this.currentStep = currentStep ?? 1;
          if (this.isAgentForm && this.currentStep === 1 && !application?.agent) {
            this.currentStep = 0;
          }
          if (this.$route.query.step === 'Summary') {
            this.currentStep = 9;
          }
          await this.formatData(application ?? {});
          this.lastSavedSections = this.snapshotSections();
          this.initAutoSave();
        } else {
          this.showSnackbar({
            text: 'Oops, Something Went Wrong!',
            color: 'error'
          });
        }
      } catch (err) {
        console.error(err);
        const { message } = err;
        if (message == 'The form has already been successfully submitted') {
          this.isFormCompleted = true;
        }
      } finally {
        this.isLoadingInit = false;
      }
    },

    onClaimantAddressSelected(address) {
      const target = this.formData.claimant;

      if (address.address1 !== undefined) target.address1 = address.address1;
      if (address.address2 !== undefined) target.address2 = address.address2;
      if (address.city !== undefined) target.city = address.city;
      if (address.state !== undefined) target.state = address.state;
      if (address.postcode !== undefined) target.postcode = address.postcode;

      this.resetClaimantVerification();
      this.debouncedVerifyClaimantAddress();
    },

    onPartnerAddressSelected(address) {
      const target = this.formData.partner;

      if (address.address1 !== undefined) target.address1 = address.address1;
      if (address.address2 !== undefined) target.address2 = address.address2;
      if (address.city !== undefined) target.city = address.city;
      if (address.state !== undefined) target.state = address.state;
      if (address.postcode !== undefined) target.postcode = address.postcode;

      this.resetPartnerVerification();
      this.debouncedVerifyPartnerAddress();
    },
    // ─── Reset verification methods ──────────────────
    resetClaimantVerification() {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      this.formData.claimant.addressValidated = null;
      this.formData.claimant.verificationStatus = null;
      this.formData.claimant.wasCleansed = false;
      this.formData.claimant.verifiedReference = null;
      this.formData.claimant.verificationDetails = null;
    },

    resetPartnerVerification() {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      this.formData.partner.addressValidated = null;
      this.formData.partner.verificationStatus = null;
      this.formData.partner.wasCleansed = false;
      this.formData.partner.verifiedReference = null;
      this.formData.partner.verificationDetails = null;
    },

    resetCompanyVerification(index) {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      const company = this.formData.claimant.ukCompanies[index];
      if (company) {
        company.addressValidated = null;
        company.verificationStatus = null;
        company.wasCleansed = false;
        company.verifiedReference = null;
        company.verificationDetails = null;
      }
    },

    resetAllCompanyVerifications() {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      this.formData.claimant.ukCompanies.forEach((company, index) => {
        this.resetCompanyVerification(index);
      });
    },

    // ─── Auto-verify debounce helpers ─────────────────
    debouncedVerifyClaimantAddress() {
      // Don't auto-verify during initial load or save
      if (this.isLoadingInit || this.isLoadingSave) {
        return;
      }

      if (this.claimantAddressDebounceTimer) {
        clearTimeout(this.claimantAddressDebounceTimer);
      }

      this.claimantAddressDebounceTimer = setTimeout(() => {
        this.autoVerifyClaimantAddress();
      }, 1500);
    },

    debouncedVerifyPartnerAddress() {
      if (this.isLoadingInit || this.isLoadingSave) {
        return;
      }

      if (this.partnerAddressDebounceTimer) {
        clearTimeout(this.partnerAddressDebounceTimer);
      }

      this.partnerAddressDebounceTimer = setTimeout(() => {
        this.autoVerifyPartnerAddress();
      }, 1500);
    },

    autoVerifyClaimantAddress() {
      const c = this.formData.claimant;

      // Only auto-verify if all required address fields are filled
      if (!c.address1 || !c.city || !c.country) {
        return;
      }

      // Don't re-verify if already successfully verified
      if (c.addressValidated === true && c.verificationStatus !== 'Invalid') {
        return;
      }

      // Don't verify if already in progress
      if (this.isValidatingAddress) {
        return;
      }

      this.verifyClaimantAddress(true);
    },

    autoVerifyPartnerAddress() {
      // Skip if partner uses same address as claimant
      if (this.formData.partner.saveAsClaimant) {
        return;
      }

      const p = this.formData.partner;

      // Only auto-verify if all required address fields are filled
      if (!p.address1 || !p.city || !p.country) {
        return;
      }

      // Don't re-verify if already successfully verified
      if (p.addressValidated === true && p.verificationStatus !== 'Invalid') {
        return;
      }

      // Don't verify if already in progress
      if (this.isValidatingAddress) {
        return;
      }

      this.verifyPartnerAddress(true);
    },

    // ─── Address Verification Methods ──────────────────
    getVerificationStatus(addressObj) {
      if (!addressObj) return 'Not verified';
      if (addressObj.addressValidated === null || addressObj.addressValidated === undefined) return 'Not verified';
      if (addressObj.addressValidated === true) {
        if (addressObj.verificationStatus === 'Partial') return 'Partial';
        if (addressObj.verificationStatus === 'Invalid') return 'Invalid';
        return 'Verified ✓';
      }
      if (addressObj.addressValidated === false) return 'Invalid ✗';
      return 'Not verified';
    },
    getVerificationIcon(addressObj) {
      if (!addressObj) return 'mdi-help-circle';
      if (addressObj.addressValidated === null || addressObj.addressValidated === undefined) return 'mdi-help-circle';
      if (addressObj.addressValidated === true) {
        if (addressObj.verificationStatus === 'Partial') return 'mdi-alert-circle';
        if (addressObj.verificationStatus === 'Invalid') return 'mdi-close-circle';
        return 'mdi-check-circle';
      }
      if (addressObj.addressValidated === false) return 'mdi-close-circle';
      return 'mdi-help-circle';
    },
    getVerificationColor(addressObj) {
      if (!addressObj) return 'grey';
      if (addressObj.addressValidated === null || addressObj.addressValidated === undefined) return 'grey';
      if (addressObj.addressValidated === true) {
        if (addressObj.verificationStatus === 'Partial') return 'warning';
        if (addressObj.verificationStatus === 'Invalid') return 'error';
        return 'success';
      }
      if (addressObj.addressValidated === false) return 'error';
      return 'grey';
    },

    async verifyAddressWithLoqate(addressObj, context, isAuto = false) {
      if (!addressObj || !addressObj.address1 || !addressObj.city || !addressObj.country) {
        if (!isAuto) {
          this.showSnackbar({
            text: 'Please fill in address fields before verification.',
            color: 'warning'
          });
        }
        return;
      }

      // Prevent concurrent verification
      if (this.isValidatingAddress) {
        return;
      }

      this.isValidatingAddress = true;

      try {
        const result = await AddressService.autoVerifyAddress(addressObj, addressObj.country || 'GBR');
        console.log(result);
        if (result.success) {
          addressObj.addressValidated = result.isValid;
          addressObj.verificationStatus = result.verificationStatus;
          addressObj.wasCleansed = result.wasCleansed;
          addressObj.verifiedReference = result.verificationDetails?.verifiedAddressReference || null;
          addressObj.verificationDetails = {
            avc: result.avc,
            aqi: result.aqi,
            matchScore: result.matchScore,
            postProcessedLevel: result.postProcessedLevel,
            preProcessedLevel: result.preProcessedLevel
          };

          if (context === 'claimant' && this.formData.partner.saveAsClaimant) {
            this.formData.partner.addressValidated = addressObj.addressValidated;
            this.formData.partner.verificationStatus = addressObj.verificationStatus;
            this.formData.partner.wasCleansed = addressObj.wasCleansed;
            this.formData.partner.verifiedReference = addressObj.verifiedReference;
            this.formData.partner.verificationDetails = addressObj.verificationDetails;
          }

          // Only show snackbar for manual verification (not auto)
          if (!isAuto) {
            this.showSnackbar({
              text: result.message + (result.isValid ? ' ✅' : ' ⚠️'),
              color: result.isValid ? 'success' : 'warning'
            });
          }
        } else {
          addressObj.addressValidated = false;
          addressObj.verificationStatus = 'Invalid';
          addressObj.wasCleansed = false;
          addressObj.verifiedReference = null;
          addressObj.verificationDetails = null;

          if (!isAuto) {
            this.showSnackbar({
              text: result.message || 'Address verification failed',
              color: 'error'
            });
          }
        }
      } catch (err) {
        console.error('Address verification error:', err);
        addressObj.addressValidated = false;
        addressObj.verificationStatus = 'Invalid';
        addressObj.wasCleansed = false;
        addressObj.verifiedReference = null;
        addressObj.verificationDetails = null;

        if (!isAuto) {
          this.showSnackbar({
            text: 'Failed to verify address. Please try again.',
            color: 'error'
          });
        }
      } finally {
        this.isValidatingAddress = false;
      }
    },

    verifyClaimantAddress(isAuto = false) {
      // Prevent duplicate calls
      if (this.isValidatingAddress) {
        return;
      }
      this.verifyAddressWithLoqate(this.formData.claimant, 'claimant', isAuto);
    },

    verifyPartnerAddress(isAuto = false) {
      // Skip if partner uses same address as claimant
      if (this.formData.partner.saveAsClaimant) {
        if (!isAuto) {
          this.showSnackbar({
            text: 'Address is same as claimant. Verify claimant address instead.',
            color: 'info'
          });
        }
        return;
      }

      if (this.isValidatingAddress) {
        return;
      }

      this.verifyAddressWithLoqate(this.formData.partner, 'partner', isAuto);
    },

    // ─── Format Data ──────────────────────────────────────
    formatData(application = {}) {
      const {
        agent,
        contactCommunication,
        partner,
        claimant,
        claimantBenefits,
        partnerBenefits,
        pension,
        benefits,
        bank,
        otherInformation
      } = application;

      if (agent) {
        this.formData.agent = agent;
      }

      if (contactCommunication) {
        this.formData.contactCommunication = {
          firstName: contactCommunication?.firstName ?? '',
          lastName: contactCommunication?.lastName ?? '',
          tel: contactCommunication?.tel ?? '',
          email: contactCommunication?.email ?? '',
          relationship: contactCommunication?.relationship ?? ''
        };
      }

      if (claimant) {
        this.formData.claimant = {
          title: claimant?.title ?? '',
          firstName: claimant?.firstName ?? '',
          surname: claimant?.surname ?? '',
          otherNames: claimant?.otherNames ?? '',
          dob: claimant?.dob ?? '',
          middleName: claimant?.middleName ?? '',
          maidenName: claimant?.maidenName ?? '',
          tel: claimant?.tel ?? '',
          email: claimant?.email ?? '',
          maritalStatus: claimant?.maritalStatus ?? '',
          nationality: claimant?.nationality ?? '',
          dateOfMarriage: claimant?.dateOfMarriage ?? '',
          dateOfBereavement: claimant?.dateOfBereavement ?? '',
          dateOfDivorce: claimant?.dateOfDivorce ?? '',
          hasNino: claimant?.hasNino ?? false,
          nino: claimant?.nino ?? '',
          // Address
          country: claimant?.country ?? '',
          postcode: claimant?.postcode ?? '',
          address1: claimant?.address1 ?? '',
          address2: claimant?.address2 ?? '',
          city: claimant?.city ?? '',
          state: claimant?.stateName ?? '',
          // Verification status
          addressValidated: claimant?.addressValidated ?? null,
          verificationStatus: claimant?.verificationStatus ?? null,
          wasCleansed: claimant?.wasCleansed ?? false,
          verifiedReference: claimant?.verifiedReference ?? null,
          verificationDetails: claimant?.verificationDetails ?? null,
          // UK history
          everLivedUK: claimant?.everLivedUK ?? false,
          livedUKFrom: claimant?.livedUKFrom ?? '',
          livedUKTo: claimant?.livedUKTo ?? '',
          approxYearsInUK: claimant?.approxYearsInUK ?? null,
          everLivedUKNotes: claimant?.everLivedUKNotes ?? '',
          everWorkedUK: claimant?.everWorkedUK ?? false,
          everWorkedUKuseSameDates: claimant?.everWorkedUKuseSameDates ?? false,
          workedUKFrom: claimant?.workedUKFrom ?? '',
          workedUKTo: claimant?.workedUKTo ?? '',
          hasProofOfUKWork: claimant?.hasProofOfUKWork ?? false,
          countriesLived: this.formatCountriesLived(claimant?.countriesLived ?? []),
          ukCompanies: this.formatUkCompanies(claimant?.ukCompanies ?? []),
          selfEmployedUK: claimant?.selfEmployedUK ?? false,
          marriedWomansStamp: claimant?.marriedWomansStamp ?? false,
          mindedKids: claimant?.mindedKids ?? false,
          children: this.formatChildren(claimant?.children ?? [])
        };
      }

      if (partner) {
        this.formData.partner = {
          title: partner?.title ?? '',
          firstName: partner?.firstName ?? '',
          surname: partner?.surname ?? '',
          otherNames: partner?.otherNames ?? '',
          dob: partner?.dob ?? '',
          maidenName: partner?.maidenName ?? '',
          hasNino: partner?.hasNino ?? false,
          nino: partner?.nino ?? '',
          saveAsClaimant: partner?.saveAsClaimant ?? false,
          everLivedUK: partner?.everLivedUK ?? false,
          everLivedUKNotes: partner?.everLivedUKNotes ?? '',
          everWorkedUK: partner?.everWorkedUK ?? false,
          claimingUKPension: partner?.claimingUKPension ?? false,
          referenceOnAnyDWPLetters: partner?.referenceOnAnyDWPLetters ?? '',
          // Address
          country: partner?.country ?? '',
          postcode: partner?.postcode ?? '',
          address1: partner?.address1 ?? '',
          address2: partner?.address2 ?? '',
          city: partner?.city ?? '',
          state: partner?.stateName ?? '',
          // Verification status
          addressValidated: partner?.addressValidated ?? null,
          verificationStatus: partner?.verificationStatus ?? null,
          wasCleansed: partner?.wasCleansed ?? false,
          verifiedReference: partner?.verifiedReference ?? null,
          verificationDetails: partner?.verificationDetails ?? null
        };
      }

      if (claimantBenefits) {
        this.formData.claimantBenefits = {
          statePension: claimantBenefits?.statePension ?? false,
          graduatedRetirement: claimantBenefits?.graduatedRetirement ?? false,
          widows: claimantBenefits?.widows ?? false,
          bereavement: claimantBenefits?.bereavement ?? false,
          isWaitingUkBenefits: claimantBenefits?.isWaitingUkBenefits ?? false,
          isWaitingUkBenefitsText: claimantBenefits?.isWaitingUkBenefitsText ?? ''
        };
      }

      if (partnerBenefits) {
        this.formData.partnerBenefits = {
          statePension: partnerBenefits?.statePension ?? false,
          graduatedRetirement: partnerBenefits?.graduatedRetirement ?? false,
          widows: partnerBenefits?.widows ?? false,
          bereavement: partnerBenefits?.bereavement ?? false,
          isWaitingUkBenefits: partnerBenefits?.isWaitingUkBenefits ?? false,
          isWaitingUkBenefitsText: partnerBenefits?.isWaitingUkBenefitsText ?? ''
        };
      }

      if (pension) {
        this.formData.pension = {
          claimingFromToday: pension?.claimingFromToday ?? false,
          startDate: pension?.startDate ?? '',
          workAfterPensionAge: pension?.workAfterPensionAge ?? false,
          deferredPension: pension?.deferredPension ?? false,
          imprisonedSincePensionAge: pension?.imprisonedSincePensionAge ?? false,
          workInUKduring2years: pension?.workInUKduring2years ?? false,
          everSelfEmployedUKafter1978: pension?.everSelfEmployedUKafter1978 ?? false,
          everPayReducedUKnino: pension?.everPayReducedUKnino ?? false
        };
      }

      if (benefits) {
        this.formData.benefits = {
          isYourPartnerCurrentlyGetPension: benefits?.isYourPartnerCurrentlyGetPension ?? false,
          isYourPartnerCurrentlyGetPensionText: benefits?.isYourPartnerCurrentlyGetPensionText ?? ''
        };
      }

      if (bank) {
        this.formData.bank = {
          accountType: bank?.accountType ?? '',
          accountOwnerName: bank?.accountOwnerName ?? '',
          ownsThisAccount: bank?.ownsThisAccount ?? '',
          accountOwnerEmail: bank?.accountOwnerEmail ?? '',
          bankName: bank?.bankName ?? '',
          sortCode: bank?.sortCode ?? '',
          iban: bank?.iban ?? '',
          bic: bank?.bic ?? '',
          account: bank?.account ?? '',
          country: bank?.country ?? (claimant?.country || 'IRL'),
          postcode: bank?.postcode ?? '',
          address: bank?.address ?? '',
          city: bank?.city ?? '',
          isEuropeCountry: bank?.isEuropeCountry ?? false
        };
      }
      if (otherInformation) {
        this.formData.otherInformation = {
          notes: otherInformation?.notes ?? '',

          isUKproperty: otherInformation?.isUKproperty ?? false,
          UkProperties: this.formatUkProperties(otherInformation?.UkProperties ?? []),

          isAccountant: otherInformation?.isAccountant ?? false,
          accountantName: otherInformation?.accountantName ?? '',
          accountantPhone: otherInformation?.accountantPhone ?? '',
          accountantAddress1: otherInformation?.accountantAddress1 ?? '',
          accountantAddress2: otherInformation?.accountantAddress2 ?? '',
          accountantCity: otherInformation?.accountantCity ?? '',
          accountantCountry: otherInformation?.accountantCountry ?? '',
          accountantPostcode: otherInformation?.accountantPostcode ?? '',

          isSelfAssesedTax: otherInformation?.isSelfAssesedTax ?? false,

          isAgePension: otherInformation?.isAgePension ?? false
        };
      }
    },

    formatUkProperties(addresses = []) {
      if (!Array.isArray(addresses)) return [];

      if (addresses?.length === 0) {
        return [];
      }

      return addresses.map((company) => ({
        country: company?.country ?? 'GBR',
        postcode: company?.postcode ?? '',
        address1: company?.address1 ?? '',
        address2: company?.address2 ?? '',
        city: company?.city ?? ''
      }));
    },

    addUkPropertie() {
      this.formData.otherInformation.UkProperties.push({
        country: 'GBR',
        postcode: '',
        address1: '',
        address2: '',
        city: ''
      });
    },

    removeUkPropertie(index) {
      this.formData.otherInformation.UkProperties.splice(index, 1);
    },

    // ── Array formatting helpers ──────────────────────────
    formatCountriesLived(countries = []) {
      if (!Array.isArray(countries)) return [];

      if (countries?.length === 0) {
        return [
          {
            country: '',
            fromDate: '',
            isLiveHereNow: false,
            toDate: '',
            socialSecurityNumber: ''
          }
        ];
      }

      return countries.map((country) => ({
        country: country?.country ?? '',
        fromDate: country?.fromDate ?? '',
        isLiveHereNow: country?.isLiveHereNow ?? false,
        toDate: country?.toDate ?? '',
        socialSecurityNumber: country?.socialSecurityNumber ?? country?.socialSecurityNum ?? ''
      }));
    },

    formatUkCompanies(companies = []) {
      if (!Array.isArray(companies)) return [];

      if (companies?.length === 0) {
        return [
          {
            name: '',
            country: 'GBR',
            postcode: '',
            address1: '',
            address2: '',
            city: '',
            fromDate: '',
            toDate: '',
            addressValidated: null,
            verificationStatus: null,
            wasCleansed: false,
            verifiedReference: null,
            verificationDetails: null
          }
        ];
      }

      return companies.map((company) => ({
        name: company?.name ?? '',
        country: company?.country ?? 'GBR',
        postcode: company?.postcode ?? '',
        address1: company?.address1 ?? '',
        address2: company?.address2 ?? '',
        city: company?.city ?? '',
        fromDate: company?.fromDate ?? '',
        toDate: company?.toDate ?? '',
        // Preserve verification status
        addressValidated: company?.addressValidated ?? null,
        verificationStatus: company?.verificationStatus ?? null,
        wasCleansed: company?.wasCleansed ?? false,
        verifiedReference: company?.verifiedReference ?? null,
        verificationDetails: company?.verificationDetails ?? null
      }));
    },

    formatChildren(children = []) {
      if (!Array.isArray(children)) return [];

      return children.map((child) => ({
        name: child?.name ?? '',
        dob: child?.dob ?? '',
        mindedFromDate: child?.mindedFromDate ?? child?.mindedFrom ?? '',
        mindedToDate: child?.mindedToDate ?? child?.mindedTo ?? '',
        nino: child?.nino ?? ''
      }));
    },

    // ── Dynamic list helpers ──────────────────────────
    addCountry() {
      this.formData.claimant.countriesLived.push({
        country: '',
        fromDate: '',
        isLiveHereNow: false,
        toDate: '',
        socialSecurityNumber: ''
      });
    },
    removeCountry(index) {
      this.formData.claimant.countriesLived.splice(index, 1);
    },

    addCompany() {
      this.formData.claimant.ukCompanies.push({
        name: '',
        country: 'GBR',
        postcode: '',
        address1: '',
        address2: '',
        city: '',
        fromDate: '',
        toDate: '',
        addressValidated: null,
        verificationStatus: null,
        wasCleansed: false,
        verifiedReference: null,
        verificationDetails: null
      });
    },
    removeCompany(index) {
      this.formData.claimant.ukCompanies.splice(index, 1);
    },

    addChild() {
      this.formData.claimant.children.push({
        name: '',
        dob: '',
        mindedFromDate: '',
        mindedToDate: '',
        nino: ''
      });
    },
    removeChild(index) {
      this.formData.claimant.children.splice(index, 1);
    },

    async verifyBank() {
      this.isLoadingBankInfo = true;
      try {
        const { response } = await ApplicationService.bankVerification({
          applicationId: this.applicationId,
          application: this.formData
        });
        this.formatBankDetails(response);
        await this.populateBankAddressFromGoogle();
      } catch (err) {
        console.error(err);
        const { message } = err;
        this.showSnackbar({
          text: message ?? 'Oops, Something Went Wrong!',
          color: 'error'
        });
      } finally {
        this.isLoadingBankInfo = false;
      }
    },
    formatBankDetails(response = {}) {
      this.formData.bank.bic = response?.BICCode || '';
      if (response?.IBAN) {
        const iban = response.IBAN.replace('IBAN ', '');
        this.formData.bank.iban = iban;
        this.formData.bank.country = iban.slice(0, 2);
      }

      this.formData.bank.bankName = response?.ShortBankName || '';
      this.formData.bank.sortCode = this.formatSortCode(response?.SortCode);
      this.formData.bank.account = response?.AccountNumber || '';
      this.formData.bank.postcode = response?.Address?.RawAddress?.Postcode || '';
      this.formData.bank.address = response?.Address?.RawAddress?.ThoroughfareName || '';
      this.formData.bank.city = response?.Address?.RawAddress?.Locality || '';
    },
    async populateBankAddressFromGoogle() {
      const bank = this.formData.bank;

      const missing = {
        city: !bank.city?.trim(),
        address: !bank.address?.trim(),
        postcode: !bank.postcode?.trim()
      };

      if (!missing.city && !missing.address && !missing.postcode) {
        return;
      }

      const hasQuery = bank.bankName || bank.address || bank.postcode || bank.city;
      if (!hasQuery) return;

      this.isLoadingBankCity = true;
      try {
        const resolved = await GooglePlacesService.findCityFromAddress(
          {
            bankName: bank.bankName,
            address1: bank.address,
            address2: '',
            city: bank.city,
            postcode: bank.postcode,
            country: bank.country
          },
          missing
        );
        console.log('resolved', resolved);

        if (!resolved) return;

        const filled = [];

        if (missing.city && resolved.city) {
          bank.city = resolved.city;
          filled.push('city');
        }
        if (missing.address && resolved.address) {
          bank.address = resolved.address;
          filled.push('address');
        }
        if (missing.postcode && resolved.postcode) {
          bank.postcode = resolved.postcode;
          filled.push('postcode');
        }
      } catch (err) {
        console.error('Google Places fallback failed:', err);
      } finally {
        this.isLoadingBankCity = false;
      }
    },
    // ── Persistence ───────────────────────────────────
    async saveStepData(step) {
      let isSaved = false;
      this.isLoadingSave = true;
      try {
        const snapshot = this.snapshotSections();
        await ApplicationService.updateApplicationDetails({
          applicationId: this.applicationId,
          currentStep: step,
          application: this.formData,
          isAgentForm: this.isAgentForm
        });
        this.lastSavedSections = snapshot;
        isSaved = true;
      } catch (err) {
        console.error(err);
        const { message } = err;
        this.showSnackbar({
          text: message ?? 'Oops, Something Went Wrong!',
          color: 'error'
        });
      } finally {
        this.isLoadingSave = false;
      }
      return isSaved;
    },

    initAutoSave() {
      const intervalTime = 60 * 1000;
      setInterval(() => {
        const autoSaveCondition =
          !this.isLoadingSave && !this.isLoadingInit && !this.isFormCompleted && this.isFormPending;

        if (autoSaveCondition) {
          this.autoSaveData();
        }
      }, intervalTime);
    },

    // One JSON string per top-level section of the form, used to find what changed since the last save
    snapshotSections() {
      const snapshot = {};
      Object.keys(this.formData).forEach((key) => {
        snapshot[key] = JSON.stringify(this.formData[key] ?? null);
      });
      return snapshot;
    },

    // Sends only the sections edited since the last save, so an idle session never overwrites
    // what another session (client / agent) has saved in the meantime.
    async autoSaveData() {
      const snapshot = this.snapshotSections();
      const changedSections = Object.keys(snapshot).filter((key) => snapshot[key] !== this.lastSavedSections[key]);
      if (!changedSections.length) {
        return;
      }
      const application = {};
      changedSections.forEach((key) => {
        application[key] = this.formData[key];
      });
      try {
        await ApplicationService.updateApplicationDetails({
          applicationId: this.applicationId,
          currentStep: this.currentStep,
          application,
          isAgentForm: this.isAgentForm,
          isAutoSave: true,
          isPartial: true
        });
        changedSections.forEach((key) => {
          this.lastSavedSections[key] = snapshot[key];
        });
        this.showSnackbar({
          text: 'Form Auto Saved',
          color: 'success'
        });
      } catch (err) {
        console.error(err);
      }
    },

    // ── Navigation ────────────────────────────────────
    async nextStep() {
      const formRef = this.$refs[`form${this.currentStep}`];
      if (formRef) {
        const { valid: isValid } = await formRef.validate();
        if (!isValid) {
          this.showSnackbar({
            text: 'Please fill in all required fields.',
            color: 'error'
          });
          return;
        }
      }
      if (this.currentStep === 0) {
        this.currentStep++;
      } else {
        const isSaved = await this.saveStepData(this.currentStep);
        if (isSaved) {
          this.currentStep++;
        }
      }
    },

    prevStep() {
      this.currentStep--;
    },

    goToStep(step) {
      this.currentStep = step;
    },

    // ── Submit ────────────────────────────────────────
    async submitForm() {
      const formRef = this.$refs[`form${this.currentStep}`];
      if (formRef) {
        const { valid: isValid } = await formRef.validate();
        if (!isValid) {
          this.showSnackbar({
            text: 'Please fill in all required fields.',
            color: 'error'
          });
          return;
        }
      }

      const isSaved = await this.saveStepData(this.currentStep);

      if (isSaved) {
        this.showSnackbar({ text: 'Form submitted successfully!', color: 'success' });
        this.isFormSuccessfullySubmited = true;
      }
    },

    toSalesforceDate(date) {
      return toLocalISODate(date);
    },

    formatSortCode(raw, country = this.formData.bank.country) {
      const value = (raw || '').replace(/\D/g, '').slice(0, 6);

      // Australian BSB: XXX-XXX
      if (country === 'AU') {
        return value.length > 3 ? value.slice(0, 3) + '-' + value.slice(3) : value;
      }

      // Default sort code: XX-XX-XX
      if (value.length > 4) {
        return value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4);
      }
      if (value.length > 2) {
        return value.slice(0, 2) + '-' + value.slice(2);
      }

      return value;
    },

    handleSortCodeChange(event) {
      this.formData.bank.sortCode = this.formatSortCode(event.target.value);
    },

    isEuropeanCountry(countryCode) {
      const europeanCountryCodes = [
        'AL',
        'AD',
        'AM',
        'AT',
        'AZ',
        'BY',
        'BE',
        'BA',
        'BG',
        'CH',
        'CY',
        'CZ',
        'DE',
        'DK',
        'EE',
        'ES',
        'FI',
        'FR',
        'GE',
        'GR',
        'HR',
        'HU',
        'IE',
        'IS',
        'IT',
        'KZ',
        'LI',
        'LT',
        'LU',
        'LV',
        'MC',
        'MD',
        'ME',
        'MK',
        'MT',
        'NL',
        'NO',
        'PL',
        'PT',
        'RO',
        'RU',
        'RS',
        'SE',
        'SI',
        'SK',
        'SM',
        'TR',
        'UA',
        'UK',
        'VA',
        'GI',
        'JE',
        'GG'
      ];
      return europeanCountryCodes.includes(countryCode.toUpperCase());
    },
    // AU has no IBAN but its BIC is still printed on the IPC1394, so the field stays visible (optional)
    shouldShowBic(countryCode) {
      return this.shouldShowIban(countryCode) || countryCode === 'AU';
    },
    shouldShowIban(countryCode) {
      const ibanCountries = [
        'AL',
        'AD',
        'AT',
        'BE',
        'BA',
        'BG',
        'HR',
        'CY',
        'CZ',
        'DK',
        'EE',
        'FO',
        'FI',
        'FR',
        'DE',
        'GI',
        'GR',
        'GL',
        'HU',
        'IS',
        'IE',
        'IL',
        'IT',
        'LV',
        'LI',
        'LT',
        'LU',
        'MK',
        'MT',
        'MU',
        'MD',
        'MC',
        'ME',
        'NL',
        'NO',
        'PL',
        'PT',
        'RO',
        'SM',
        'SA',
        'RS',
        'SK',
        'SI',
        'ES',
        'SE',
        'CH',
        'TN',
        'TR',
        'AE',
        'GB',
        'VA',
        'VG',
        'IRL'
      ];

      // If country is selected and is in IBAN list, show IBAN fields
      if (countryCode && ibanCountries.includes(countryCode.toUpperCase())) {
        return true;
      }

      // If country is not selected yet or not in IBAN list, hide IBAN fields
      return false;
    }
  }
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.address-validation-chip {
  font-size: 11px;
  font-weight: 600;
}

:deep(.v-chip--size-small) {
  font-size: 11px;
  height: 24px;
}

:deep(.v-chip--size-x-small) {
  font-size: 10px;
  height: 20px;
}

.text-caption {
  font-size: 12px;
}
/* Step Indicators */
.steps-indicators {
  display: flex;
  justify-content: space-between;
  position: relative;
  margin-bottom: 20px;
}

.steps-indicators::before {
  content: '';
  position: absolute;
  top: 10px;
  left: 20px;
  right: 20px;
  height: 2px;
  background: #e9ecef;
  z-index: 0;
}

.step-dot {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  flex: 1;
}

.step-number {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.5rem;
  transition: all 0.3s ease;
  margin-bottom: 8px;
}

.step-dot.active .step-number {
  background: #4f46e5;
  color: white;
}

.step-dot.completed .step-number {
  background: #179d88;
  color: white;
}

.step-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-align: center;
  font-weight: 500;
  transition: color 0.3s ease;
}

.step-dot.active .step-label {
  color: #1f2937;
  font-weight: 600;
}
</style>
